import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import {
  type Plan, type RunningTask, type RunProgress, type FinishedRun, type NodePresence, type SessionRef, type AuditEvent, type ChatMessage, type ChatStream, type Mission, type Organization,
  type OwnerCommand, type OwnerCommandWorkerRecipient, type Project, type Repository, type Reader, type ReportMessage, type RuntimeOutage, type RuntimeRecheckInstruction, type Worker,
  type CapacityGuardContractRow, type LaunchCatalogRow, type MissionTask, type TaskActionRow, type WaitingJobRecoveryRow, type NodeCapacityRow, type NodePressureRow,
  type NodeRelease, type NodeResourceSampleRow, type NodeVersion,
  type WorkforceSession, type NodeOffering, type ModelOfferingRef, type WorkerOfferingPreference, type RuntimeProof,
  type CommandPlannerHostStatus, type WorkerTerminalRow, type CommandPlanRow, type WorkerRoutingRow, type ProviderUsageLatest, type AiUsagePolicyRow, type AiConnectionStatusRow, type AiLocalRuntimeStatusRow, type CodexAutonomyStatusRow,
  type UsageMeasurementRow, type RunPoolUsageRow, type ApprovalPolicyRow,
  type ObserverThreadRow, type ObserverMessageRow,
} from '../lib/data';
import {
  brunelActivityIndicator, cockpitAttentionCount, cockpitLiveStatusEvents, cockpitWorkerAttentions, cockpitWorkerCards,
  cockpitWorkerStatus, withRunActivity, withRunDepartment,
} from '../lib/cockpit-view';
import { liveDockCensus, withRecordedWorkerPhases } from '../lib/activity-cues';
import {
  LIVE_DOCK_FRESHNESS_ALLOWANCE_MS,
  liveDockChannelStopped,
  liveDockDelivery,
} from '../lib/live-dock-freshness';
import { activeRosterEntries } from '../lib/active-worker-projection';
import { startVisibleInterval } from '../lib/visible-interval';
import { delayWhileVisible, mainCommandPollActive } from '../lib/main-command-poll';
import { withCanonicalMachine } from '../lib/worker-board';
import { routingLockLabel } from '../lib/routing-view';
import { brunelAppVersion, brunelBuildId, brunelVersionLabel } from '../lib/app-version';
import { brunelSupportUrl } from '../lib/support-link';
import { CockpitWorkers, type CockpitMachineMeta } from './CockpitWorkers';
import { LiveDock } from './LiveDock';
import { InspectorWorkbench } from './InspectorWorkbench';
import { DeveloperNotepad } from './DeveloperNotepad';
import { NoteHandoffChipView } from './NoteHandoffChip';
import type { NoteHandoffStage } from '../lib/note-handoff';
import { decideIdeLaunch } from '@brunel/shared';
import { inspectorSelectionFromWorker, latestResultForWorker } from '../lib/ide-view';
import { RoadmapPanel } from './RoadmapPanel';
import { WorkBoard } from './WorkBoard';
import { buildWorkBoard, type WorkBoardCard } from '../lib/work-board';
import {
  EMPTY_INSPECTOR_SELECTION, clampInspectorWidth, effectiveInspectorMode, inspectorRailWidthPx,
  recallInspector, rememberInspector,
  type InspectorMode, type InspectorSelection, type InspectorTab,
} from '../lib/inspector';
import { OwnerAttentionPanel } from './OwnerAttentionPanel';
import { ManagementAttention } from './ManagementAttention';
import { LiveActivityLane } from './LiveActivityLane';
import { WorkerStatusIndicator } from './WorkerStatusIndicator';
import '../cockpit.css';
import { useOpenWorkspace, useRoutingScope, useStopRun, useTerminalControl, useWorkerOffering } from '../lib/mutations';
import { workerOfferingChoice, type WorkerOfferingChoice } from '../lib/worker-offering-choice';
import { planAttention } from '../lib/plans';
import { commandsCoveringPlans, proposalArchive } from '../lib/proposal-archive';
import { commandThread } from '../lib/command-chat';
import { planOutcomes } from '../lib/outcome';
import { answerFor } from '../lib/answers';
import type { WorkforceSnapshot } from '../lib/workforce';
import { sendsOnEnter } from '../lib/composing';
import { composerHistoryDirection } from '../lib/composer-history';
import { withNudge } from '../lib/nudge';
import { snapshot } from '../lib/stats';
import { currentFailures, mergedAssignmentsFrom } from '../lib/current-failures';
import { durationPhrase, handoverPhrase, runDurationMs, runTaskTitle, taskTitles, toolPhrase, workerPhrase } from '../lib/finished';
import { MERGED_AWAITING_OWNER, ownerGateEvidence, presentOwnerGateWaiting } from '../lib/owner-gate-waiting';
import { presentRecentRuns, type RecentRunView } from '../lib/recent-run-supersession';
import { SnapshotStrip } from './Workload';
import { cockpitAiAdvisories, cockpitAiMeters } from '../lib/ai-usage-view';
import { connectionFromPresenceRead, lastSeenLabel, workerBadge, type OperationalStatus } from '../lib/presence';
import { appendDictation, speechFor, unspoken } from '../lib/speech';
import { DictateButton, ReadAloudButton, speakText, stopSpeaking } from './Voice';
import { ModeToggle } from './ModeToggle';
import {
  buildSharedCommandTimeline,
  commandDiscussionMode,
  type CommandDiscussionMode,
  type SharedTimelineItem,
} from '../lib/shared-command-timeline';
import { parseReplyRef, replyDraftForItem, type MainCommandReplyDraft, type MainCommandReplyRef } from '../lib/main-command-reply';
import { mainCommandHasUnsent, saveMainCommandForReload, takeMainCommandAfterReload } from '../lib/main-command-reload';
import { sessionStore, useReloadCheck, useReloadPreserver } from '../lib/safe-reload';
import { MachineCapacityPanel } from './MachineCapacityPanel';
import { AI_CONNECTIONS_DISPLAY } from '../lib/ai-connections-catalog';
import {
  DEFAULT_CAPACITY_GUARD_CONTRACT, buildMachineCapacity,
  type CapacityGuardContract, type MachineCapacity,
  type LiveActivityRow,
} from '@brunel/shared';
import { ownerNeededAdded, ownerNeededKeys, ownerNeededPlaybackPlan } from '../lib/owner-needed';
import { AutopilotHeartbeatStatus, type AutopilotHeartbeatRead } from './RoadmapAutopilot';
import { AutopilotHeartbeatSettings } from './AutopilotHeartbeatSettings';
import { loadLiveActivity } from '../lib/live-activity';
import { Rail } from './Rail';
import { ContextSettings, ContextSwitcher } from './ContextSwitcher';
import { WorkflowGuide } from './WorkflowGuide';
import { NotificationSoundSettings } from './NotificationSoundSettings';
import { plainTerminalText } from '../lib/terminal-plain-text';
import { getSharedNotificationPlayer } from '../lib/notification-playback';
import { buildNotificationSnapshots, dispatchNotificationTransitions } from '../lib/notification-wire';
import type { NotificationWorkerSnapshot } from '../lib/notification-events';
import { Watchdog } from './Watchdog';
import { outOfTouchWorkerHosts, watchdogBubble, type WatchdogLink } from '../lib/watchdog';
import { CommandChat } from './CommandChat';
import {
  approvedPlanWorkerRequestBlocks,
  workerRequestFactsFromReadiness,
  type WorkerRequestBlock,
} from '../lib/worker-request-blocks';
import type { MainCommandOrganizerTool } from './MainCommandConversationTabs';
import { FloatingMainCommand } from './FloatingMainCommand';
import { activeMiniConversation } from '../lib/main-command-mini';
import { useMainCommandConversationController } from '../lib/main-command-controller';
import {
  useComposerDraft,
  useMainCommandComposerController,
  type MainCommandComposerController,
} from '../lib/main-command-composer-controller';
import { ResetPasswordForm } from './SignIn';
import { subscribeTerminalRealtime, type TerminalTransportState } from '../lib/terminal-attachment';
import { WorkerTerminal } from './WorkerTerminal';
import { PlanDrawer } from './Plans';
import { AddNode, ConnectionsPanel, RulesPanel } from './Presence';
import { RemovedComputers } from './RemoveComputer';
import { OutagesPanel } from './Outages';
import { AiUsageDetail } from './AiUsageDetail';
import { latestProgressForRun } from './RunProgressLine';
import { WorkerRunLog } from './WorkerRunLog';
import { AiUsagePolicySettings } from './AiUsagePolicySettings';
import { ConnectorCenter } from './ConnectorCenter';
import { DatabaseHealthStrip } from './DatabaseHealthStrip';
import { EMPTY_CONNECTOR_REGISTRY, loadConnectorRegistry } from '../lib/connector-registry';
import { buildSystemsMonitorCards, type SystemConnectorType } from '../lib/systems-monitor';
import { EngineRoom } from './EngineRoom';
import { EngineRoomRecords } from './EngineRoomRecords';
import { EngineRoomHistory } from './EngineRoomHistory';
import { EngineRoomViewSelector } from './EngineRoomViewSelector';
import { AiSessions } from './AiSessions';
import { MachineBoardPage, useMachineBoardLink } from './MachineBoardPage';
import { buildEngineRoom } from '../lib/engine-room';
import { engineRoomMachines } from '../lib/engine-room-machines';
import {
  formatMachinistForMainCommand, machinistOverseerReport, runMachinist,
  type MachinistReport,
} from '../lib/machinist';
import { NodeUpdates, useComputerRollout } from './NodeUpdates';
import { GithubMergeRouteSettings } from './GithubMergeRouteSettings';
import { ProjectRepositorySettings } from './ProjectRepositorySettings';
import { AiExecutionProfileStrip, type AiExecutionProfileApplyResult, type AiExecutionProfileChange } from './AiExecutionProfileStrip';
import {
  applyAiProfileChange, overseerAiProfile, workerAiProfile, type AiExecutionProfileView,
} from '../lib/ai-execution-profile';
import { discardOffer, discardWorker, type DiscardResult } from '../lib/discard-worker';
import { discardTask } from '../lib/discard-task';
import { requestRuntimeRecheck } from '../lib/runtime-recheck';
import { ProfileSecuritySettings } from './ProfileSecuritySettings';
import { loadWorkBoardHistory } from '../lib/work-board-history';
import { CURRENT_FAILURES_ID, ReportsPanel, workerHasJob } from './ReportsPanel';
import type { AiRuntimeCapabilityRow, OwnerCommandControl, WorkerAiExecutionProfileRow } from '../lib/data';
import { ReplyTargetBar } from './ReplyTargetBar';
import { MainCommandMentionMenu } from './MainCommandMentionMenu';
import {
  buildMainCommandMentionCandidates,
  mainCommandMentionSuggestions,
  removeSelectedMainCommandMention,
  resolveMainCommandMentions,
  type MainCommandMentionCandidate,
  type MainCommandRecipientGroup,
} from '../lib/main-command-mentions';
import { workerRouteTargets, workerSeats } from '../lib/plan-routing';
import { WhoDoesWhat } from './WhoDoesWhat';
import { SettingsGroup, SettingsSectionNav } from './SettingsGroups';
import type { AiConnectionRoleBindingRow } from '../lib/ai-role-bindings';
import {
  SELECTABLE_REPLY_TARGETS,
  buildMainCommandReplyIdentities,
  directReplySelection,
  toggledAllWorkers,
  toggledReplyTargets,
  type MainCommandDirectRecipient,
  type MainCommandReplyTarget,
} from '../lib/main-command-recipients';
import {
  allMainCommandRecipientsSettled,
  loadMainCommandDeliveryState,
  mainCommandRecipientStatuses,
  overseerConversationCommands,
  unreconciledMainCommandEchoes,
  type MainCommandAcceptedEcho,
  type MainCommandDeliverySnapshot,
} from '../lib/main-command-delivery';
import { mainCommandNextRereadMs } from '../lib/main-command-reply-watch';
import {
  workerChatParticipants,
  workerChatReadiness,
  workerChatLabel,
  type PersistentAiStateSnapshot,
} from '../lib/worker-chat-readiness';
import { runStats } from '../lib/runs';
import { recallRailPinned, rememberRailPinned } from '../lib/rail';
import {
  nextWorkerRunLog, recallWorkerTerminalDebug, rememberWorkerTerminalDebug, workerSurfaceOpeningView,
  workerSurfaceViews, workerTerminalShown, workerTerminalViewLabel,
  WORKER_SURFACE_VIEW_LABEL, type WorkerRunLog as WorkerRunLogState, type WorkerSurfaceView,
} from '../lib/worker-run-log';
import {
  COMPOSER_DEFAULT_PX, SECTIONS, SIDE_MIN_PX, applySectionReveal, browserStore,
  clampComposerHeight, clampSideWidth, isCommandWebRoot, landingReveal, landingSection, recallLayout,
  rememberLayout, revealChosenSection, sectionBadges, showPanel,
  type SectionId,
} from '../lib/sections';
import {
  SENT_MESSAGE, advanceReceipt, commandReceiptAction, failedDeliveryReason, receiptForCommandAction, receiptFromSubmitRefusal, sameReceipt, sentReceipt,
  type ControlReceiptView,
} from '../lib/control-receipt';
import { ControlReceiptNote } from './ControlReceiptNote';

interface RoomData {
  projects: readonly Project[];
  repositories: readonly Repository[];
  workers: readonly Worker[];
  missions: readonly Mission[];
  commands: readonly OwnerCommand[];
  workerRecipients: readonly OwnerCommandWorkerRecipient[];
  workerChatMessages: readonly ChatMessage[];
  plans: readonly Plan[];
  planHistory: readonly Plan[];
  /** The tasks approved plans became, which is how a turn learns what came of one. */
  missionTasks: readonly MissionTask[];
  /** #671: what the owner can do with each task, as brunel.task_actions states it. */
  taskActions: readonly TaskActionRow[];
  /** Jobs Brunel closed without running them, or moved, with the one line saying why. */
  jobRecoveries: readonly WaitingJobRecoveryRow[];
  /** Assignments whose pull request durable evidence shows merged (lib/current-failures). */
  mergedAssignments: readonly string[];
  results: readonly FinishedRun[];
  running: readonly RunningTask[];
  progress: readonly RunProgress[];
  audit: readonly AuditEvent[];
  /** The routine machine chatter, held apart from the trail the panel opens on. */
  auditChatter: readonly AuditEvent[];
  reports: readonly ReportMessage[];
  streams: readonly ChatStream[];
  nodes: readonly NodePresence[];
  approvalPolicy: ApprovalPolicyRow | null;
  sessions: readonly SessionRef[];
  outages: readonly RuntimeOutage[];
  /** Check again answers already recorded for each computer. Display only. */
  recheckInstructions: readonly RuntimeRecheckInstruction[];
  pressure: readonly NodePressureRow[];
  capacity: readonly NodeCapacityRow[];
  /** The numbers a sustained load guard is made of, as the database states them (#483). */
  capacityContract: CapacityGuardContract | null;
  /** The newest bounded window of resource readings, for each machine's own small history. */
  resourceSamples: readonly NodeResourceSampleRow[];
  nodeVersions: readonly NodeVersion[];
  nodeReleases: readonly NodeRelease[];
  launchCatalog: readonly LaunchCatalogRow[];
  workforceSessions: readonly WorkforceSession[];
  nodeOfferings: readonly NodeOffering[];
  modelOfferings: readonly ModelOfferingRef[];
  workerOfferingPreferences: readonly WorkerOfferingPreference[];
  runtimeProofs: readonly RuntimeProof[];
  plannerHostStatus: readonly CommandPlannerHostStatus[];
  /** One row per Worker with everything its terminal knows - the Cockpit card's source. */
  workerTerminals: readonly WorkerTerminalRow[];
  /** The plans Main Command is working through, read once here for the whole screen. */
  commandPlans: readonly CommandPlanRow[];
  /** The standing routing instruction per project: which Worker new work goes to. */
  workerRouting: readonly WorkerRoutingRow[];
  providerUsage: readonly ProviderUsageLatest[];
  usageMeasurements: readonly UsageMeasurementRow[];
  runPoolUsage: readonly RunPoolUsageRow[];
  aiUsagePolicy: AiUsagePolicyRow | null;
  aiConnectionStatuses: readonly AiConnectionStatusRow[];
  aiConnectionRoleBindings: readonly AiConnectionRoleBindingRow[];
  aiLocalRuntimeStatuses: readonly AiLocalRuntimeStatusRow[];
  codexAutonomyStatuses: readonly CodexAutonomyStatusRow[];
  nodeIdeAdapters: readonly import('../lib/ide-view').IdeAdapterRow[];
  workspaceOpenTargets: readonly import('../lib/ide-view').WorkspaceOpenTargetRow[];
  observerThreads: readonly ObserverThreadRow[];
  observerMessages: readonly ObserverMessageRow[];
}

/**
 * The sustained-guard contract as a shape the shared capacity model reads. A read that
 * did not land leaves the published defaults in place rather than quietly using a
 * different window than the database does.
 */
function capacityContractRow(row: CapacityGuardContractRow | null): CapacityGuardContract | null {
  if (row === null) return null;
  return {
    windowSeconds: row.window_seconds,
    minSamples: row.min_samples,
    releaseMarginPercent: row.release_margin_percent,
    cooldownSeconds: row.cooldown_seconds,
  };
}

interface ObserverReadState {
  readonly nodes: boolean;
  readonly pressure: boolean;
  readonly capacity: boolean;
  readonly resourceSamples: boolean;
  readonly workers: boolean;
  readonly terminals: boolean;
  readonly plans: boolean;
  readonly providerUsage: boolean;
  readonly outages: boolean;
  readonly aiUsagePolicy: boolean;
}

const UNKNOWN_OBSERVER_READS: ObserverReadState = {
  nodes: false, pressure: false, capacity: false,
  resourceSamples: false, workers: false, terminals: false, plans: false, providerUsage: false, outages: false,
  aiUsagePolicy: false,
};

const EMPTY: RoomData = { projects: [], repositories: [], workers: [], missions: [], commands: [], workerRecipients: [], workerChatMessages: [], plans: [], planHistory: [], missionTasks: [], taskActions: [], jobRecoveries: [], mergedAssignments: [], results: [], running: [], progress: [], audit: [], auditChatter: [], reports: [], streams: [], nodes: [], approvalPolicy: null, sessions: [], outages: [], pressure: [], capacity: [], capacityContract: null, resourceSamples: [], nodeVersions: [], nodeReleases: [], launchCatalog: [], workforceSessions: [], nodeOfferings: [], modelOfferings: [], workerOfferingPreferences: [], runtimeProofs: [], plannerHostStatus: [], workerTerminals: [], commandPlans: [], workerRouting: [], providerUsage: [], usageMeasurements: [], runPoolUsage: [], aiUsagePolicy: null, aiConnectionStatuses: [], aiConnectionRoleBindings: [], aiLocalRuntimeStatuses: [], codexAutonomyStatuses: [], nodeIdeAdapters: [], workspaceOpenTargets: [], observerThreads: [], observerMessages: [], recheckInstructions: [] };
/** Main Command is the conversation with the Overseer; finding discussions are gone. */
const COMMAND_MODE: CommandDiscussionMode = commandDiscussionMode();
/** Approvals were removed: no Worker waits on one. */
const NO_PENDING_APPROVALS: ReadonlyMap<string, number> = new Map();

/**
 * How often the whole desk is read again.
 *
 * One pass is thirty-odd reads across as many views, and almost none of what it asks about
 * - projects, rules, releases, the launch catalogue, finished runs - changes between one
 * pass and the next. Every ten seconds it was six of those passes a minute per open browser
 * against a database that is also running the Overseer; half a minute is honest for the
 * slow half of the screen, and the fast half has its own beat below.
 */
const REFRESH_MS = 30_000;
const MAIN_COMMAND_DELIVERY_HYDRATION_LIMIT = 24;
/**
 * How often the Cockpit looks at the Workers and their terminals, which move faster.
 *
 * Two reads, not thirty, so this can stay quick - but three seconds was quicker than a
 * Worker's state ever actually changes, and each pass is two joined views, twenty times a
 * minute, for every browser left open on this screen. The terminal itself does not wait on
 * this: live output arrives on its own channel, and the node broadcasts a terminal's state
 * the moment it changes.
 */
const COCKPIT_REFRESH_MS = LIVE_DOCK_FRESHNESS_ALLOWANCE_MS;

export function Brand() {
  return (
    <span className="brand">
      <span className="brand-mark" aria-hidden="true">B</span>
      <span className="brand-name">BRUNEL <span className="brand-by">Command</span></span>
    </span>
  );
}

/** Collapsible section of a column. */
function Panel({ title, count, tone, closed, children }: { title: string; count?: number; tone?: "attention"; closed?: boolean; children: React.ReactNode }) {
  return (
    <details className={tone ? `panel tone-${tone}` : "panel"} open={!closed}>
      <summary><h2>{title}</h2>{count !== undefined && count > 0 && <span className="count">{count}</span>}</summary>
      <div className="panel-body">{children}</div>
    </details>
  );
}

function rowsOr<T>(result: { ok: true; rows: readonly T[] } | { ok: false }): readonly T[] {
  return result.ok ? result.rows : [];
}

export function OperationsRoom(props: {
  client: SupabaseClient; reader: Reader; orgs: readonly Organization[]; guard: Guard;
  owner: { verified: boolean; verify: () => void }; onSignOut: () => void;
  nodeConnection: { url: string; publishableKey: string };
  /** Where the planner listens, so a new command can wake it at once. */
  plannerUrl: string;
  /** Signed-in account identity. Profile & Security keeps it and does not render it. */
  accountEmail: string | null;
}) {
  const { client, reader, orgs, guard, owner, onSignOut, nodeConnection, plannerUrl, accountEmail } = props;
  const [store] = useState(() => browserStore());
  const [remembered] = useState(() => recallLayout(store));
  const [orgId, setOrgId] = useState(() => orgs.find((o) => o.id === remembered.orgId)?.id ?? orgs[0]!.id);
  // #856: what an update reload carried across for this organization, read once.
  const [reloaded] = useState(() => takeMainCommandAfterReload(sessionStore(), orgId));
  const [data, setData] = useState<RoomData>(EMPTY);
  const [streamId, setStreamId] = useState<string | null>(null);
  const [chat, setChat] = useState<readonly ChatMessage[]>([]);
  const [voiceReplies, setVoiceReplies] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Taken when node_presence arrives. Cockpit ticks move nowMs and must not age that row.
  const [presenceReadAtMs, setPresenceReadAtMs] = useState(() => Date.now());
  const [lastFullRefreshAt, setLastFullRefreshAt] = useState<number | null>(null);
  const [lastFreshAt, setLastFreshAt] = useState<number | null>(null);
  const [channelClosedOrErrored, setChannelClosedOrErrored] = useState(false);
  const lastFreshAtRef = useRef<number | null>(null);
  const channelStoppedRef = useRef(false);
  const deliveryStartedAt = useRef<number | null>(null);
  if (deliveryStartedAt.current === null) deliveryStartedAt.current = nowMs;
  const terminalTransports = useRef(new Map<string, TerminalTransportState>());
  // Every Worker keeps one terminal surface mounted; this selects which one the
  // Worker board focuses (#592). It is a view choice and routes nothing.
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(null);
  /** Whether the selected Worker's terminal is presented. Opening it never touches the PTY. */
  const [workerTerminalOpen, setWorkerTerminalOpen] = useState(false);
  const [section, setSectionState] = useState<SectionId>(() => landingSection(
    typeof window === 'undefined' ? '/' : window.location.pathname,
    remembered.section,
  ));
  const setSection = useCallback((next: SectionId) => {
    setSectionState(next);
    if (typeof window === 'undefined') return;
    applySectionReveal(window, revealChosenSection(window.location.hash));
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || !isCommandWebRoot(window.location.pathname)) return;
    const reveal = landingReveal(window.location.pathname, window.location.hash);
    if (reveal === null) return;
    applySectionReveal(window, reveal);
  }, []);
  const [engineRoomView, setEngineRoomView] = useState(remembered.engineRoomView);
  const [projectId, setProjectId] = useState(remembered.projectId ?? '');
  const [draft, setDraft] = useState<{ text: string; at: number } | null>(
    () => reloaded === null ? null : { text: reloaded.content, at: Date.now() },
  );
  /** Selected developer note staged into Main Command. Not ambient context. */
  const [noteHandoff, setNoteHandoff] = useState<NoteHandoffStage | null>(reloaded?.noteHandoff ?? null);
  // #743: the one exact message the owner is replying to, shown above the composer.
  const [replyingTo, setReplyingTo] = useState<MainCommandReplyDraft | null>(reloaded?.replyingTo ?? null);
  const [commandReplyRefs, setCommandReplyRefs] = useState<ReadonlyMap<string, MainCommandReplyRef>>(new Map());
  const chatMode = COMMAND_MODE;
  const [replyTargets, setReplyTargets] = useState<Set<MainCommandReplyTarget>>(
    () => new Set(reloaded?.replyTargets ?? ['overseer']),
  );
  const [workerReplyTargets, setWorkerReplyTargets] = useState<Set<string>>(() => new Set(reloaded?.workerReplyTargets ?? []));
  const [persistentWorkerSessions, setPersistentWorkerSessions] = useState<readonly PersistentAiStateSnapshot[]>([]);
  const [workerChatHeardNodes, setWorkerChatHeardNodes] = useState<Set<string>>(() => new Set());
  const [acceptedMainCommands, setAcceptedMainCommands] = useState<readonly MainCommandAcceptedEcho[]>([]);
  const [commandReceiptBook, setCommandReceiptBook] = useState<{
    readonly pending: ControlReceiptView | null;
    readonly byCommand: ReadonlyMap<string, ControlReceiptView>;
  }>({ pending: null, byCommand: new Map() });
  // What Brunel said each sent message became, so a new job is not shown as steering.
  const [commandReceiptActions, setCommandReceiptActions] = useState<ReadonlyMap<string, string>>(() => new Map());
  const markCommandSent = useCallback(() => {
    setCommandReceiptBook((current) => ({ ...current, pending: sentReceipt() }));
  }, []);
  const bindCommandReceipt = useCallback((accepted: MainCommandAcceptedEcho) => {
    setCommandReceiptBook((current) => {
      const base = current.pending ?? sentReceipt();
      const next = advanceReceipt(base, receiptForCommandAction({
        control: null,
        deliveries: [...accepted.managementDeliveries, ...accepted.workerDeliveries].map((record) => ({
          state: record.state ?? record.status ?? '',
          reason: record.reason ?? record.reasonCode ?? record.reason_code ?? null,
        })),
      }));
      const byCommand = new Map(current.byCommand);
      byCommand.set(accepted.commandId, next);
      return { pending: null, byCommand };
    });
    setCommandReceiptActions((current) => new Map(current).set(accepted.commandId, commandReceiptAction(accepted)));
  }, []);
  const [mainCommandDeliveries, setMainCommandDeliveries] = useState<ReadonlyMap<string, MainCommandDeliverySnapshot>>(
    () => new Map(),
  );
  const mainCommandReconcileGeneration = useRef<Map<string, number>>(new Map());
  const [reviewingPlans, setReviewingPlans] = useState(false);
  const [liveDockExpanded, setLiveDockExpanded] = useState(false);
  const [mainCommandTool, setMainCommandTool] = useState<MainCommandOrganizerTool>(null);
  const [liveActivity, setLiveActivity] = useState<readonly LiveActivityRow[]>([]);
  const [floatingMainCommandExpanded, setFloatingMainCommandExpanded] = useState(false);
  const [sideWidth, setSideWidth] = useState<number | null>(remembered.sideWidth);
  // How tall the owner has made the command box. Null is the default height - modest, a
  // few lines - since the box is for writing a goal, not for reading the desk back.
  const [composerHeight, setComposerHeight] = useState<number | null>(remembered.composerHeight);
  const [railPinned, setRailPinned] = useState(() => recallRailPinned(store));
  // Owner decision 2026-10-04: a Worker's interactive terminal is a debug view, off unless
  // this browser turns it on. A view choice only; it starts, stops and routes nothing.
  const [workerTerminalDebug, setWorkerTerminalDebug] = useState(() => recallWorkerTerminalDebug(store));
  const [workflowGuideOpen, setWorkflowGuideOpen] = useState(false);
  const [inspectorRemembered] = useState(() => recallInspector(store));
  const [inspectorMode, setInspectorMode] = useState<InspectorMode>(inspectorRemembered.mode);
  const [inspectorWidth, setInspectorWidth] = useState(inspectorRemembered.widthPx);
  const [selectedUsageKey, setSelectedUsageKey] = useState<string | null>(null);
  const [usageFocusRequest, setUsageFocusRequest] = useState(0);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>(inspectorRemembered.tab);
  const [railLabels, setRailLabels] = useState(inspectorRemembered.railLabels);
  const [inspectorLive, setInspectorLive] = useState<InspectorSelection>(EMPTY_INSPECTOR_SELECTION);
  const [roadmapItems, setRoadmapItems] = useState<readonly import('../lib/data').RoadmapItemRow[]>([]);
  const [roadmapReadOk, setRoadmapReadOk] = useState(false);
  // The permanent owner rules Rules shows. Kept through a read that fails: a rule that is
  // permanent does not vanish from the screen because one refresh could not load it.
  const [ownerRules, setOwnerRules] = useState<readonly import('../lib/data').OwnerRuleRow[]>([]);
  const [roadmapFocusId, setRoadmapFocusId] = useState<string | null>(null);
  // The Overseer heartbeat; null until it can actually be read.
  const [autopilotHeartbeat, setAutopilotHeartbeat] = useState<AutopilotHeartbeatRead | null>(null);
  const [observerReads, setObserverReads] = useState<ObserverReadState>(UNKNOWN_OBSERVER_READS);
  const [connectorRegistryState, setConnectorRegistryState] = useState(() => ({
    orgId,
    snapshot: EMPTY_CONNECTOR_REGISTRY,
  }));
  const connectorRegistry = connectorRegistryState.orgId === orgId
    ? connectorRegistryState.snapshot
    : EMPTY_CONNECTOR_REGISTRY;
  const connectorRegistryOrg = useRef(orgId);
  connectorRegistryOrg.current = orgId;
  const [selectedSystemType, setSelectedSystemType] = useState<SystemConnectorType>('supabase_project');
  /** Which computer's Machine Capacity detail the right drawer is showing (#483). */
  const [selectedMachineId, setSelectedMachineId] = useState<string | null>(null);
  const [viewportPx, setViewportPx] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1440);
  const desk = useRef<HTMLElement | null>(null);
  const shell = useRef<HTMLDivElement | null>(null);
  const topbar = useRef<HTMLElement | null>(null);
  const spokenTurns = useRef<Set<string> | null>(null);
  const notificationSnapshots = useRef<readonly NotificationWorkerSnapshot[]>([]);
  const ownerNeededSnapshot = useRef<ReadonlySet<string> | null>(null);
  const terminalControl = useTerminalControl(client);
  const workBoardStopRun = useStopRun(client);
  const openWorkspace = useOpenWorkspace(client);
  const [ideLaunchOk, setIdeLaunchOk] = useState<string | null>(null);
  const routingScope = useRoutingScope(client);
  const closeWorkflowGuide = useCallback(() => setWorkflowGuideOpen(false), []);
  /** Opening a Worker's terminal is the one way into its surface, wherever the owner asked from. */
  const expandWorker = useCallback((workerId: string) => {
    setExpandedWorkerId(workerId);
    setWorkerTerminalOpen(true);
    setSection('control');
  }, []);
  const closeWorkerTerminal = useCallback(() => setWorkerTerminalOpen(false), []);

  const refreshConnectorRegistry = useCallback(async () => {
    const next = await loadConnectorRegistry(client, orgId);
    if (connectorRegistryOrg.current === orgId) {
      setConnectorRegistryState({ orgId, snapshot: next });
    }
  }, [client, orgId]);

  /** Publish a Cockpit receipt. Broadcasts update the ref without a room render while delivery is still fresh. */
  const publishFreshness = useCallback((at: number, fresh: boolean) => {
    if (fresh && (lastFreshAtRef.current === null || at >= lastFreshAtRef.current)) lastFreshAtRef.current = at;
    setNowMs(at);
    setLastFreshAt(lastFreshAtRef.current);
  }, []);

  const refresh = useCallback(async () => {
    setObserverReads(UNKNOWN_OBSERVER_READS);
    const connectorRegistryRead = refreshConnectorRegistry();
    const ownerRulesRead = reader.ownerRules().catch(() => null);
    const heartbeatRead = Promise.all([reader.autopilotHeartbeat(orgId), reader.autopilotLatestRound(orgId)]).catch(() => null);
    const liveActivityRead = loadLiveActivity(client, orgId);
    // Stamp when this presence response arrives, not when the rest of the desk finishes.
    let presenceReadAt = Date.now();
    const nodePresenceRead = reader.nodePresence(orgId).then((rows) => {
      presenceReadAt = Date.now();
      return rows;
    });
    const [projects, repositories, workers, missions, commands, workerRecipientRows, audit, auditChatter, reports, streams, workerChatMessageRows, nodes, policies, sessions, plans, results, running, progress, planHistoryRows, missionTaskRows, outages, pressure, capacity, capacityContractRows, resourceSampleRows, nodeVersions, nodeReleases, launchCatalog, workforceSessions, nodeOfferings, modelOfferings, workerOfferingPreferences, runtimeProofs, plannerHostStatusRows, workerTerminalRows, commandPlanRows, workerRoutingRows, roadmapItemRows, providerUsageRows, usageMeasurementRows, runPoolUsageRows, aiUsagePolicyRows, aiConnectionStatusRows, aiConnectionRoleBindingRows, aiLocalRuntimeStatusRows, codexAutonomyStatusRows, taskActionRows] = await Promise.all([
      reader.projects(orgId), reader.repositories(orgId), reader.workers(orgId), reader.missions(orgId), reader.ownerCommands(orgId), reader.ownerCommandWorkerRecipients(orgId),
      reader.auditEvents(orgId), reader.routineAuditEvents(orgId), reader.reports(orgId),
      reader.chatStreams(orgId), reader.workerChatMessages(orgId),
      nodePresenceRead, reader.approvalPolicies(orgId), reader.sessions(orgId), reader.plans(orgId), reader.runSummaries(orgId), reader.runningTasks(orgId), reader.runningProgress(orgId), reader.planHistory(orgId), reader.missionTasks(orgId), reader.runtimeOutages(orgId), reader.nodePressure(orgId), reader.nodeCapacity(orgId), reader.capacityGuardContract(), reader.nodeResourceSamples(orgId), reader.nodeVersions(orgId), reader.nodeReleases(orgId), reader.launchCatalog(orgId), reader.workforceSessions(orgId), reader.nodeOfferings(orgId), reader.modelOfferings(orgId), reader.workerOfferingPreferences(orgId), reader.runtimeProofs(orgId), reader.commandPlannerHostStatus(orgId),
      reader.workerTerminals(orgId), reader.commandPlans(orgId), reader.workerRouting(orgId), reader.roadmapItems(orgId),
      reader.providerUsageLatest(orgId), reader.usageMeasurements(orgId), reader.runPoolUsage(orgId), reader.aiUsagePolicies(orgId), reader.aiConnectionStatuses(orgId), reader.aiConnectionRoleBindings(orgId), reader.aiLocalRuntimeStatuses(orgId), reader.codexAutonomyStatuses(orgId),
      reader.taskActions(orgId),
    ]);
    let recheckInstructionRows: readonly RuntimeRecheckInstruction[] = [];
    try {
      recheckInstructionRows = rowsOr(await reader.runtimeRecheckInstructions(orgId));
    } catch {
      recheckInstructionRows = [];
    }
    // A database that does not have the table yet has no such jobs.
    let jobRecoveryRows: readonly WaitingJobRecoveryRow[] = [];
    try {
      jobRecoveryRows = rowsOr(await reader.waitingJobRecoveries(orgId));
    } catch {
      jobRecoveryRows = [];
    }
    let mergedAssignmentIds: readonly string[] = [];
    try {
      const [mergedPrs, mergeActions] = await Promise.all([
        reader.mergedPullRequests(orgId), reader.mergeCompletionActions(orgId),
      ]);
      mergedAssignmentIds = mergedAssignmentsFrom(rowsOr(mergedPrs), rowsOr(mergeActions));
    } catch {
      mergedAssignmentIds = [];
    }
    const commandRows = await commandsCoveringPlans(
      rowsOr(commands),
      [...rowsOr(plans), ...rowsOr(planHistoryRows)],
      (ids) => reader.ownerCommandsByIds(orgId, ids),
    );
    setPresenceReadAtMs(presenceReadAt);
    setData((previous) => ({
      projects: rowsOr(projects), repositories: rowsOr(repositories), workers: rowsOr(workers), missions: rowsOr(missions), commands: commandRows,
      workerRecipients: rowsOr(workerRecipientRows),
      workerChatMessages: rowsOr(workerChatMessageRows),
      audit: rowsOr(audit), auditChatter: rowsOr(auditChatter), reports: rowsOr(reports),
      streams: rowsOr(streams),
      nodes: rowsOr(nodes), approvalPolicy: rowsOr(policies)[0] ?? null, sessions: rowsOr(sessions), plans: rowsOr(plans), planHistory: rowsOr(planHistoryRows), missionTasks: rowsOr(missionTaskRows), taskActions: rowsOr(taskActionRows), jobRecoveries: jobRecoveryRows, mergedAssignments: mergedAssignmentIds, results: rowsOr(results), running: rowsOr(running), progress: rowsOr(progress), outages: rowsOr(outages), recheckInstructions: recheckInstructionRows, pressure: rowsOr(pressure), capacity: rowsOr(capacity),
      capacityContract: capacityContractRow(rowsOr(capacityContractRows)[0] ?? null),
      resourceSamples: rowsOr(resourceSampleRows),
      nodeVersions: rowsOr(nodeVersions), nodeReleases: rowsOr(nodeReleases), launchCatalog: rowsOr(launchCatalog), workforceSessions: rowsOr(workforceSessions), nodeOfferings: rowsOr(nodeOfferings), modelOfferings: rowsOr(modelOfferings), workerOfferingPreferences: rowsOr(workerOfferingPreferences), runtimeProofs: rowsOr(runtimeProofs),
      plannerHostStatus: rowsOr(plannerHostStatusRows),
      workerTerminals: rowsOr(workerTerminalRows), commandPlans: rowsOr(commandPlanRows),
      workerRouting: rowsOr(workerRoutingRows),
      providerUsage: rowsOr(providerUsageRows),
      usageMeasurements: rowsOr(usageMeasurementRows),
      runPoolUsage: rowsOr(runPoolUsageRows),
      aiUsagePolicy: rowsOr(aiUsagePolicyRows)[0] ?? null,
      aiConnectionStatuses: rowsOr(aiConnectionStatusRows),
      aiConnectionRoleBindings: rowsOr(aiConnectionRoleBindingRows),
      aiLocalRuntimeStatuses: rowsOr(aiLocalRuntimeStatusRows),
      codexAutonomyStatuses: rowsOr(codexAutonomyStatusRows),
      nodeIdeAdapters: previous.nodeIdeAdapters,
      workspaceOpenTargets: previous.workspaceOpenTargets,
      observerThreads: previous.observerThreads,
      observerMessages: previous.observerMessages,
    }));
    setRoadmapItems(rowsOr(roadmapItemRows));
    setRoadmapReadOk(roadmapItemRows.ok);
    const ownerRulesResult = await ownerRulesRead;
    if (ownerRulesResult?.ok) setOwnerRules(ownerRulesResult.rows);
    const heartbeatRows = await heartbeatRead;
    setAutopilotHeartbeat(heartbeatRows !== null && heartbeatRows[0].ok && heartbeatRows[1].ok
      ? { heartbeat: heartbeatRows[0].rows[0] ?? null, latestRound: heartbeatRows[1].rows[0] ?? null }
      : null);
    setLiveActivity(await liveActivityRead);
    await connectorRegistryRead;
    setObserverReads((current) => ({
      ...current,
      nodes: nodes.ok,
      pressure: pressure.ok,
      capacity: capacity.ok,
      resourceSamples: resourceSampleRows.ok,
      workers: workers.ok,
      terminals: workerTerminalRows.ok,
      plans: plans.ok,
      providerUsage: providerUsageRows.ok,
      outages: outages.ok,
      aiUsagePolicy: aiUsagePolicyRows.ok,
    }));
    try {
      const [observerThreadRows, observerMessageRows, ideAdapterRows, workspaceTargetRows] = await Promise.all([
        reader.observerThreads(orgId),
        reader.observerMessages(orgId),
        reader.nodeIdeAdapters(orgId),
        reader.workspaceOpenTargets(orgId),
      ]);
      setData((prev) => ({
        ...prev,
        nodeIdeAdapters: rowsOr(ideAdapterRows),
        workspaceOpenTargets: rowsOr(workspaceTargetRows),
        observerThreads: rowsOr(observerThreadRows),
        observerMessages: rowsOr(observerMessageRows),
      }));
    } catch {
      /* A failed read keeps what the desk already shows. */
    }
    const refreshedAt = Date.now();
    setLastFullRefreshAt(refreshedAt);
    publishFreshness(refreshedAt, workerTerminalRows.ok);
    if (streamId) {
      const messages = await reader.chatMessages(orgId, streamId);
      setChat(rowsOr(messages));
    }
  }, [reader, orgId, streamId, client, refreshConnectorRegistry, publishFreshness]);

  const computerRollout = useComputerRollout({
    client,
    guard,
    orgId,
    versions: data.nodeVersions,
    releases: data.nodeReleases,
    running: data.running,
    onChanged: refresh,
  });

  /** A bounded Main Command reread, intentionally separate from the room-wide refresh. */
  const refreshMainCommand = useCallback(async () => {
    const [commands, workerRecipientRows, workerChatMessageRows, plans, planHistoryRows, observerThreadRows, observerMessageRows] = await Promise.all([
      reader.ownerCommands(orgId),
      reader.ownerCommandWorkerRecipients(orgId),
      reader.workerChatMessages(orgId),
      reader.plans(orgId),
      reader.planHistory(orgId),
      reader.observerThreads(orgId),
      reader.observerMessages(orgId),
    ]);
    const commandRows = await commandsCoveringPlans(
      rowsOr(commands),
      [...rowsOr(plans), ...rowsOr(planHistoryRows)],
      (ids) => reader.ownerCommandsByIds(orgId, ids),
    );
    const snapshot = {
      commands: commandRows,
      workerRecipients: rowsOr(workerRecipientRows),
      workerChatMessages: rowsOr(workerChatMessageRows),
      plans: rowsOr(plans),
      planHistory: rowsOr(planHistoryRows),
      observerThreads: rowsOr(observerThreadRows),
      observerMessages: rowsOr(observerMessageRows),
    };
    setData((current) => ({ ...current, ...snapshot }));
    setNowMs(Date.now());
    return snapshot;
  }, [reader, orgId]);

  useEffect(() => {
    let stopped = false;
    const run = () => {
      void refresh().catch(() => {
        if (!stopped) publishFreshness(Date.now(), false);
      });
    };
    run();
    // #862: a background tab stops re-reading the room and catches up when shown again.
    const stopTimer = startVisibleInterval(run, REFRESH_MS);
    return () => {
      stopped = true;
      stopTimer();
    };
  }, [refresh, publishFreshness]);

  // The Workers and their terminals move faster than the rest of the desk, so the Cockpit
  // reads those views on their own beat while a job is running or a draft is waiting.
  // While the desk is visible and idle, the room refresh above already reads them.
  const mainCommandFeedsHot = useMemo(() => {
    const activity = mainCommandPollActive({
      runningCount: data.running.length,
      commandPlanStatuses: data.commandPlans.map((plan) => plan.status),
      planStatuses: data.plans.map((plan) => plan.status),
    });
    return activity.jobRunning || activity.draftWaiting;
  }, [data.running, data.commandPlans, data.plans]);
  const refreshCockpit = useCallback(async () => {
    // What each run has printed is the Worker's live log, so it is read on this faster beat
    // too. A read that fails keeps what was already shown rather than emptying a log.
    const [workerTerminalRows, commandPlanRows, workerRoutingRows, providerUsageRows, aiUsagePolicyRows, workerChatMessageRows, activityRows, runningRows, progressRows] = await Promise.all([
      reader.workerTerminals(orgId), reader.commandPlans(orgId), reader.workerRouting(orgId),
      reader.providerUsageLatest(orgId), reader.aiUsagePolicies(orgId), reader.workerChatMessages(orgId),
      loadLiveActivity(client, orgId),
      reader.runningTasks(orgId), reader.runningProgress(orgId),
    ]);
    setData((current) => ({
      ...current,
      ...(runningRows.ok && progressRows.ok ? { running: runningRows.rows, progress: progressRows.rows } : {}),
      workerTerminals: rowsOr(workerTerminalRows),
      commandPlans: rowsOr(commandPlanRows),
      workerRouting: rowsOr(workerRoutingRows),
      providerUsage: rowsOr(providerUsageRows),
      aiUsagePolicy: rowsOr(aiUsagePolicyRows)[0] ?? null,
      workerChatMessages: rowsOr(workerChatMessageRows),
    }));
    setLiveActivity(activityRows);
    setObserverReads((current) => ({
      ...current,
      terminals: workerTerminalRows.ok,
      providerUsage: providerUsageRows.ok,
      aiUsagePolicy: aiUsagePolicyRows.ok,
    }));
    const refreshedAt = Date.now();
    publishFreshness(refreshedAt, workerTerminalRows.ok);
  }, [reader, orgId, client, publishFreshness]);

  useEffect(() => {
    if (!mainCommandFeedsHot) return undefined;
    let stopped = false;
    const run = () => {
      void refreshCockpit().catch(() => {
        if (!stopped) publishFreshness(Date.now(), false);
      });
    };
    const stopTimer = startVisibleInterval(run, COCKPIT_REFRESH_MS);
    return () => {
      stopped = true;
      stopTimer();
    };
  }, [refreshCockpit, publishFreshness, mainCommandFeedsHot]);

  // The rail hangs under the top bar and the work is sized against it, so the
  // frame measures the bar rather than assuming a height it may not keep when
  // it wraps on a narrow screen.
  useEffect(() => {
    const sync = () => {
      const height = topbar.current?.offsetHeight;
      if (height && shell.current) shell.current.style.setProperty('--topbar-h', `${height}px`);
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [owner.verified, section]);

  // Only project commands are turned into plans, so a project is picked by default -
  // the one the owner last worked in when it is still there.
  useEffect(() => {
    if (data.projects.length === 0) return;
    if (!data.projects.some((p) => p.id === projectId)) setProjectId(data.projects[0]!.id);
  }, [data.projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // The review closes itself once the last plan has been decided.
  useEffect(() => {
    if (reviewingPlans && data.plans.length === 0) setReviewingPlans(false);
  }, [reviewingPlans, data.plans.length]);

  // The owner comes back to the section, organization and project they left in.
  useEffect(() => {
    rememberLayout(store, {
      section, engineRoomView, orgId, projectId: projectId === '' ? null : projectId,
      sideWidth, composerHeight, commandHeight: null,
    });
  }, [store, section, engineRoomView, orgId, projectId, sideWidth, composerHeight]);

  // Inspector shell prefs survive a reload the same way the rail and cockpit layout do.
  useEffect(() => {
    rememberInspector(store, {
      mode: inspectorMode,
      widthPx: clampInspectorWidth(inspectorWidth),
      tab: inspectorTab,
      followSelection: true,
      pinnedWorkerId: null,
      pinnedTaskId: null,
      sidebarCollapsed: true,
      railLabels,
    });
  }, [store, inspectorMode, inspectorWidth, inspectorTab, railLabels]);

  useEffect(() => {
    const onResize = () => setViewportPx(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Follow Selection: expanding a Worker updates the live Inspector context without
  // redesigning Worker cards — expand is already the owner's selection signal.
  useEffect(() => {
    if (expandedWorkerId === null) {
      setInspectorLive(EMPTY_INSPECTOR_SELECTION);
      return;
    }
    const row = data.workerTerminals.find((item) => item.worker_id === expandedWorkerId);
    const worker = data.workers.find((item) => item.id === expandedWorkerId);
    const result = latestResultForWorker(expandedWorkerId, row?.task_id ?? null, data.results);
    const next = inspectorSelectionFromWorker({
      workerId: expandedWorkerId,
      worker,
      terminal: row,
      result,
    });
    setInspectorLive(next);
  }, [expandedWorkerId, data.workerTerminals, data.workers, data.results]);

  // Dragging the divider sets the right-hand Worker column's width; Command keeps the rest.
  const dragTo = useCallback((clientX: number) => {
    const box = desk.current?.getBoundingClientRect();
    if (!box) return;
    setSideWidth(clampSideWidth(box.right - clientX, box.width));
  }, []);
  const onDividerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragTo(event.clientX);
  };
  const onDividerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) dragTo(event.clientX);
  };
  const nudgeDivider = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowLeft' ? 24 : event.key === 'ArrowRight' ? -24 : 0;
    if (step === 0) return;
    event.preventDefault();
    const box = desk.current?.getBoundingClientRect();
    const width = box?.width ?? SIDE_MIN_PX * 3;
    setSideWidth((current) => clampSideWidth((current ?? width / 2) + step, width));
  };

  // A width remembered from the old command-dominant layout may be narrower than the
  // new Cockpit allows. Normalize it once the Control desk exists so an old 300px choice
  // cannot silently undo the 50/50 redesign on the next visit.
  useEffect(() => {
    if (section !== 'control' || sideWidth === null) return;
    const box = desk.current?.getBoundingClientRect();
    if (!box) return;
    const next = clampSideWidth(sideWidth, box.width);
    if (next !== sideWidth) setSideWidth(next);
  }, [section, sideWidth]);

  const togglePin = () => {
    setRailPinned((current) => {
      rememberRailPinned(store, !current);
      return !current;
    });
  };

  const toggleWorkerTerminalDebug = () => {
    setWorkerTerminalDebug((current) => {
      rememberWorkerTerminalDebug(store, !current);
      return !current;
    });
  };

  const inspectorSelection = inspectorLive;
  const ideDecision = decideIdeLaunch({
    nodeId: inspectorSelection.nodeId,
    nodeName: inspectorSelection.nodeName,
    projectId: inspectorSelection.projectId,
    adapters: data.nodeIdeAdapters.map((row) => ({
      nodeId: row.node_id,
      nodeName: data.workspaceOpenTargets.find((target) => target.node_id === row.node_id)?.node_name
        ?? inspectorSelection.nodeName
        ?? row.node_id,
      adapterKey: row.adapter_key,
      displayName: row.display_name,
      available: row.available,
    })),
    targets: data.workspaceOpenTargets.map((row) => ({
      nodeId: row.node_id,
      nodeName: row.node_name,
      projectId: row.project_id,
      projectName: row.project_name,
      hasFolder: row.has_folder,
      nodeConnected: row.node_connected,
    })),
  });
  const inspectorVisibleMode = effectiveInspectorMode(inspectorMode, viewportPx);
  const inspectorRailPx = inspectorRailWidthPx(railLabels);
  const inspectorDisplayWidth = clampInspectorWidth(inspectorWidth, viewportPx, inspectorRailPx);
  const systems = useMemo(
    () => buildSystemsMonitorCards(connectorRegistry.connectors),
    [connectorRegistry.connectors],
  );
  // #650 Discarded (retired) Workers keep their history rows; active surfaces use this list.
  const activeWorkers = useMemo(() => data.workers.filter((worker) => !worker.retired_at), [data.workers]);
  const proposalArchiveResult = useMemo(() => proposalArchive({
    plans: [...data.planHistory, ...data.plans],
    commands: data.commands,
    tasks: data.missionTasks,
    nowMs,
  }), [data.planHistory, data.plans, data.commands, data.missionTasks, nowMs]);
  const attentionPlans = useMemo(
    () => data.plans.filter((plan) => !proposalArchiveResult.archived.has(plan.id)),
    [data.plans, proposalArchiveResult],
  );
  const plans = planAttention(attentionPlans);
  const outOfTouch = data.nodes.filter((n) => connectionFromPresenceRead({ lastSeenAt: n.last_seen_at, status: n.status as OperationalStatus, presenceReadAtMs }) !== 'connected');
  // A viewer-only computer offline is shown on its card, not raised as an alarm: see outOfTouchWorkerHosts.
  const outOfTouchAlerts = outOfTouchWorkerHosts(outOfTouch, {
    capacity: data.capacity, workerNodeIds: data.workerTerminals.map((row) => row.node_id),
  });
  const latest = data.results.slice(0, 12);
  const recentRunViews = useMemo(() => presentRecentRuns(data.results), [data.results]);
  const ownerGateWaiting = useMemo(() => {
    const applied = systems.find((card) => card.typeKey === 'supabase_project')
      ?.facts.find((fact) => fact.id === 'migration_applied')?.value ?? null;
    return presentOwnerGateWaiting(
      data.results.map((row) => ({
        assignmentId: row.assignment_id,
        success: row.success,
        summary: row.summary,
        outputTail: row.output_tail,
        reportedAt: row.reported_at,
      })),
      ownerGateEvidence({
        appliedMigrationTip: applied,
        nodes: data.nodeVersions.map((row) => ({
          name: row.name,
          runningVersion: row.running_version,
          updateState: row.update_state,
          updateVersion: row.update_version,
        })),
        publishedNodeReleases: data.nodeReleases.map((row) => ({ version: row.version })),
      }),
    );
  }, [systems, data.results, data.nodeVersions, data.nodeReleases]);
  const titles = useMemo(() => taskTitles(data.missionTasks), [data.missionTasks]);
  const stats = snapshot({
    results: data.results, running: data.running, workers: activeWorkers, approvals: [],
    plans: data.plans.filter((plan) => !proposalArchiveResult.archived.has(plan.id)), nowMs,
  });
  // The header's failure count and the list it opens are this one array (lib/current-failures).
  const failures = useMemo(() => currentFailures({
    runs: data.results,
    tasks: [
      ...data.missionTasks.map((task) => ({ project_id: task.project_id, task_id: task.id, status: task.status })),
      ...data.taskActions,
    ],
    mergedAssignments: data.mergedAssignments,
    nowMs,
  }), [data.results, data.missionTasks, data.taskActions, data.mergedAssignments, nowMs]);
  const project = data.projects.find((p) => p.id === projectId) ?? null;
  const stageExistingWork = useCallback((work: {
    readonly title: string;
    readonly projectId: string | null;
    readonly projectName: string | null;
  }) => {
    const exactProject = work.projectId === null
      ? null
      : data.projects.find((candidate) => candidate.id === work.projectId) ?? null;
    if (exactProject !== null) setProjectId(exactProject.id);
    setNoteHandoff(null);
    setDraft({
      text: [
        `Work on the existing ${work.projectName ?? exactProject?.name ?? 'project'} item “${work.title}”.`,
        'Use its canonical roadmap item and task chain; do not create a duplicate. Inspect current state, then plan and dispatch the next eligible implementation step.',
      ].join('\n\n'),
      at: Date.now(),
    });
    setReplyTargets(new Set(['overseer']));
    setWorkerReplyTargets(new Set());
    setSection('control');
  }, [data.projects]);
  const stageWorkBoardCard = useCallback((card: WorkBoardCard) => {
    stageExistingWork({ title: card.title, projectId: card.projectId ?? null, projectName: card.projectName });
  }, [stageExistingWork]);

  const [workBoardStopReceipt, setWorkBoardStopReceipt] = useState<ControlReceiptView | null>(null);
  const stopWorkBoardRun = useCallback(async (assignmentId: string) => {
    setWorkBoardStopReceipt(sentReceipt());
    await guard('post_owner_command', async () => {
      if (await workBoardStopRun.stop(orgId, assignmentId, 'Stopped from Work Board by the owner')) {
        await refresh();
        return;
      }
      setWorkBoardStopReceipt(receiptFromSubmitRefusal(workBoardStopRun.lastRefusal.current ?? ''));
    });
  }, [guard, orgId, refresh, workBoardStopRun]);

  const retryWorkBoardTask = useCallback(async (
    action: TaskActionRow,
    onResult: (result: { readonly ok: boolean; readonly message: string }) => void,
  ) => {
    await guard('post_owner_command', async () => {
      let answer: { ok?: boolean; error?: { code?: string } } | null = null;
      let transportFailed = false;
      try {
        const response = await client.rpc('retry_task', {
          request: { orgId: action.org_id, projectId: action.project_id, taskId: action.task_id },
        });
        answer = response.data as { ok?: boolean; error?: { code?: string } } | null;
        transportFailed = response.error !== null;
      } catch {
        transportFailed = true;
      }

      const reread = await reader.taskAction(action.org_id, action.project_id, action.task_id).catch(() => null);
      const current = reread !== null && reread.ok ? reread.rows[0] ?? null : null;
      if (current?.status === 'waiting') {
        onResult({ ok: true, message: 'Task returned to the canonical queue and stays with its recorded Worker context.' });
      } else if (answer?.ok === false && !transportFailed) {
        const code = answer.error?.code ?? 'internal';
        onResult({
          ok: false,
          message: code === 'not_found'
            ? 'This task is no longer failed or already moved on. Work Board has been refreshed.'
            : code === 'forbidden'
              ? 'Only the verified owner can retry this task.'
              : 'Brunel refused the retry. Work Board has been refreshed.',
        });
      } else if (answer?.ok === true && !transportFailed) {
        onResult({ ok: true, message: 'Brunel accepted the retry. Work Board has been refreshed.' });
      } else {
        onResult({ ok: false, message: 'Brunel could not confirm the retry. The canonical task state has been refreshed.' });
      }
      await refresh();
    });
  }, [client, guard, reader, refresh]);
  const selectOrganization = useCallback((nextOrgId: string) => {
    if (nextOrgId === orgId) return;
    setOrgId(nextOrgId);
    setProjectId('');
    setStreamId(null);
    setChat([]);
    setReplyTargets(new Set(['overseer']));
    setWorkerReplyTargets(new Set<string>());
    setPersistentWorkerSessions([]);
    setWorkerChatHeardNodes(new Set<string>());
    setAcceptedMainCommands([]);
    setMainCommandDeliveries(new Map());
    mainCommandReconcileGeneration.current.clear();
    if (noteHandoff !== null) {
      setNoteHandoff(null);
      setDraft({ text: '', at: Date.now() });
    }
  }, [orgId, noteHandoff]);
  const replyIdentities = useMemo(() => buildMainCommandReplyIdentities({
    plannerHost: data.plannerHostStatus[0] ?? null,
    nodes: data.nodes,
    nodeOfferings: data.nodeOfferings,
    offerings: data.modelOfferings,
    catalog: data.launchCatalog,
    outages: data.outages,
    connectionStatuses: data.aiConnectionStatuses,
    nowMs,
  }), [
    data.plannerHostStatus, data.nodes, data.nodeOfferings,
    data.modelOfferings, data.launchCatalog, data.outages, data.aiConnectionStatuses, nowMs,
  ]);
  const workerChatState = useMemo(() => workerChatReadiness({
    nowMs,
    workers: data.workers,
    workerTerminals: data.workerTerminals,
    workforceSessions: data.workforceSessions,
    nodes: data.nodes,
    nodeOfferings: data.nodeOfferings,
    modelOfferings: data.modelOfferings,
    runtimeOutages: data.outages,
    persistentSessions: persistentWorkerSessions,
    heardNodes: workerChatHeardNodes,
  }), [
    nowMs, data.workers, data.workerTerminals, data.workforceSessions, data.nodes,
    data.nodeOfferings, data.modelOfferings, data.outages, persistentWorkerSessions, workerChatHeardNodes,
  ]);
  const workerParticipants = useMemo(() => workerChatParticipants(workerChatState), [workerChatState]);
  const eligibleWorkerIds = useMemo(
    () => new Set(workerParticipants.map((worker) => worker.workerId)),
    [workerParticipants],
  );
  const mentionCandidates = useMemo(() => buildMainCommandMentionCandidates({
    workers: workerChatState,
    eligibleWorkerIds,
  }), [workerChatState, eligibleWorkerIds]);
  // #564: the exact Worker/computer/runtime an approved plan will be pinned to, for the approval card.
  const planRouteTargets = useMemo(() => workerRouteTargets({
    readiness: workerChatState,
    preferences: data.workerOfferingPreferences,
    nodeOfferings: data.nodeOfferings,
    modelOfferings: data.modelOfferings,
  }), [workerChatState, data.workerOfferingPreferences, data.nodeOfferings, data.modelOfferings]);
  // The Workers a held draft can still be given when the server named none: an open session in
  // the draft's project, on a computer that has the AI the owner named.
  const planSeats = useMemo(() => workerSeats({
    workers: data.workers,
    sessions: data.workforceSessions,
    nodes: data.nodes,
    preferences: data.workerOfferingPreferences,
    nodeOfferings: data.nodeOfferings,
    modelOfferings: data.modelOfferings,
  }), [data.workers, data.workforceSessions, data.nodes, data.workerOfferingPreferences, data.nodeOfferings, data.modelOfferings]);
  const workerLabels = useMemo(
    () => Object.fromEntries(data.workers.map((worker) => [worker.id, workerChatLabel(worker.number, worker.name)])),
    [data.workers],
  );

  // Reload reconstructs recent delivery truth from the canonical #553 RPC.
  // The window is deliberately bounded; older conversation remains intact but
  // is not assigned a delivery state the browser has not just observed.
  const deliveryHydrationKey = useMemo(
    () => data.commands.slice(0, MAIN_COMMAND_DELIVERY_HYDRATION_LIMIT).map((command) => command.id).join('|'),
    [data.commands],
  );
  useEffect(() => {
    const commandIds = deliveryHydrationKey === '' ? [] : deliveryHydrationKey.split('|');
    if (commandIds.length === 0) return;
    let active = true;
    void Promise.all(commandIds.map((commandId) =>
      loadMainCommandDeliveryState(client, orgId, commandId))).then((snapshots) => {
      if (!active) return;
      setMainCommandDeliveries((current) => {
        const next = new Map(current);
        for (const snapshot of snapshots) {
          if (snapshot !== null) next.set(snapshot.commandId, snapshot);
        }
        return next;
      });
    });
    return () => { active = false; };
  }, [client, orgId, deliveryHydrationKey]);

  const reconcileAcceptedMainCommand = useCallback((accepted: MainCommandAcceptedEcho) => {
    const generations = mainCommandReconcileGeneration.current;
    const generation = (generations.get(accepted.commandId) ?? 0) + 1;
    generations.set(accepted.commandId, generation);
    const isCurrent = () => generations.get(accepted.commandId) === generation;
    const finish = () => {
      if (isCurrent()) generations.delete(accepted.commandId);
    };
    const initialDelivery: MainCommandDeliverySnapshot = {
      commandId: accepted.commandId,
      managementDeliveries: accepted.managementDeliveries,
      workerDeliveries: accepted.workerDeliveries,
    };
    setMainCommandDeliveries((current) => new Map(current).set(accepted.commandId, initialDelivery));
    const initial = mainCommandRecipientStatuses({
      ...initialDelivery,
      targets: accepted.targets,
      workerIds: accepted.workerIds,
      workerLabels,
    });
    if (allMainCommandRecipientsSettled(initial)) {
      finish();
      return;
    }

    // #644: fast rereads first, then a bounded slower watch while a reply is still pending,
    // so a slow local Observer's reply moves pending -> replied without the room poll.
    const acceptedAt = Date.now();
    const attempt = async (made: number): Promise<void> => {
      if (!isCurrent()) return;
      const [rows, delivery] = await Promise.all([
        refreshMainCommand(),
        loadMainCommandDeliveryState(client, orgId, accepted.commandId),
      ]);
      if (!isCurrent()) return;
      const canonicalDelivery = delivery ?? initialDelivery;
      if (delivery !== null) {
        setMainCommandDeliveries((current) => new Map(current).set(delivery.commandId, delivery));
      }
      const statuses = mainCommandRecipientStatuses({
        ...canonicalDelivery,
        targets: accepted.targets,
        workerIds: accepted.workerIds,
        workerLabels,
        plans: [...rows.planHistory, ...rows.plans],
        observerMessages: rows.observerMessages,
        workerMessages: rows.workerChatMessages,
      });
      const next = mainCommandNextRereadMs({
        attempt: made + 1,
        elapsedMs: Date.now() - acceptedAt,
        settled: allMainCommandRecipientsSettled(statuses),
      });
      if (next === null) {
        finish();
        return;
      }
      void delayWhileVisible(next).then(() => { void attempt(made + 1); });
    };
    void delayWhileVisible(500).then(() => { void attempt(0); });
  }, [client, orgId, refreshMainCommand, workerLabels]);

  const onMainCommandAccepted = useCallback((accepted: MainCommandAcceptedEcho) => {
    setAcceptedMainCommands((current) => [
      ...current.filter((item) => item.commandId !== accepted.commandId && item.requestId !== accepted.requestId),
      accepted,
    ].slice(-MAIN_COMMAND_DELIVERY_HYDRATION_LIMIT));
    reconcileAcceptedMainCommand(accepted);
  }, [reconcileAcceptedMainCommand]);

  useEffect(() => {
    // Before the first read there is no list to check against; a selection carried across a
    // reload (#856) waits for it rather than being cleared by the empty placeholder.
    if (data.workers === EMPTY.workers) return;
    const listed = new Set(data.workers.map((worker) => worker.id));
    setWorkerReplyTargets((current) => {
      const next = new Set([...current].filter((workerId) => listed.has(workerId)));
      if (next.size === current.size && [...next].every((workerId) => current.has(workerId))) return current;
      return next;
    });
  }, [data.workers]);

  // Each reply-target chip toggles only itself, so the owner can send to any combination.
  const toggleReplyTarget = useCallback((target: MainCommandReplyTarget) => {
    setReplyTargets((current) => toggledReplyTargets(current, target));
  }, []);
  const toggleWorkerReplyTarget = useCallback((workerId: string) => {
    setWorkerReplyTargets((current) => {
      const next = new Set(current);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  }, []);
  const toggleAllWorkerReplyTargets = useCallback(() => {
    setWorkerReplyTargets((current) => toggledAllWorkers(current, eligibleWorkerIds));
  }, [eligibleWorkerIds]);
  const selectRecipientGroup = useCallback((group: MainCommandRecipientGroup) => {
    if (group === 'management') {
      setReplyTargets(new Set(SELECTABLE_REPLY_TARGETS));
      setWorkerReplyTargets(new Set());
      return;
    }
    if (group === 'all_workers') {
      setReplyTargets(new Set());
      setWorkerReplyTargets(new Set(eligibleWorkerIds));
      return;
    }
    setReplyTargets(new Set(SELECTABLE_REPLY_TARGETS));
    setWorkerReplyTargets(new Set(eligibleWorkerIds));
  }, [eligibleWorkerIds]);
  const selectMentionRecipient = useCallback((candidate: MainCommandMentionCandidate) => {
    if (candidate.kind === 'group') {
      selectRecipientGroup(candidate.group);
      return;
    }
    if (candidate.kind === 'management') {
      setReplyTargets((current) => {
        if (current.size === 1 && current.has('overseer') && workerReplyTargets.size === 0) {
          return new Set([candidate.target]);
        }
        return new Set(current).add(candidate.target);
      });
      return;
    }
    setWorkerReplyTargets((current) => new Set(current).add(candidate.workerId));
    if (replyTargets.size === 1 && replyTargets.has('overseer') && workerReplyTargets.size === 0) {
      setReplyTargets(new Set());
    }
  }, [replyTargets, workerReplyTargets, selectRecipientGroup]);

  // The Cockpit's own reading of rows the desk already holds: a card per Worker, the
  // moments that need the owner, and what changed. No second fetch, no terminal scraping.
  // #584 the active roster shows online Workers only (Idle included); offline/history
  // Workers keep their records but never populate the board. Each card sits under the
  // computer canonical readiness names for it, even before its terminal row says so.
  // A Worker in a headless run has no terminal row to take its activity time from, so its
  // card also counts the run's own recorded start and newest recorded output.
  // The department on a card is the one its run in progress names, read with that run.
  const workerCards = useMemo(
    () => withRunDepartment(withRunActivity(withCanonicalMachine(activeRosterEntries(
      cockpitWorkerCards(data.workerTerminals, { pendingByWorker: NO_PENDING_APPROVALS, nowMs }),
      workerChatState,
    ), workerChatState), data.running, data.progress, nowMs), data.running),
    [data.workerTerminals, nowMs, workerChatState, data.running, data.progress],
  );
  const activityIndicator = useMemo(
    () => brunelActivityIndicator(workerCards, nowMs),
    [workerCards, nowMs],
  );
  const phasedWorkerCards = useMemo(
    () => withRecordedWorkerPhases(workerCards, liveActivity),
    [workerCards, liveActivity],
  );
  const dockCensus = useMemo(
    () => liveDockCensus(workerCards, data.results, nowMs),
    [workerCards, data.results, nowMs],
  );
  const liveDelivery = useMemo(
    () => liveDockDelivery({
      nowMs,
      lastFreshAt: lastFreshAtRef.current,
      awaitingSinceMs: deliveryStartedAt.current ?? nowMs,
      refreshIntervalMs: mainCommandFeedsHot ? COCKPIT_REFRESH_MS : REFRESH_MS,
      channelClosedOrErrored,
    }),
    [nowMs, lastFreshAt, channelClosedOrErrored, mainCommandFeedsHot],
  );
  // Owner direction (#700, 2026-09-29): the second column is a report of the work, and Workers
  // appear while they have a job. Idle Workers stay configured, one click away.
  const [sideView, setSideView] = useState<'reports' | 'activity' | 'workers'>('reports');
  const [showIdleWorkers, setShowIdleWorkers] = useState(false);
  const jobWorkerCards = useMemo(
    () => phasedWorkerCards.filter((card) => workerHasJob(card, data.running)),
    [phasedWorkerCards, data.running],
  );
  const idleWorkerCount = phasedWorkerCards.length - jobWorkerCards.length;
  const visibleWorkerCards = useMemo(
    () => showIdleWorkers ? phasedWorkerCards : phasedWorkerCards.filter((card) => workerHasJob(card, data.running)
      || (workerTerminalOpen && card.workerId === expandedWorkerId)),
    [showIdleWorkers, phasedWorkerCards, data.running, workerTerminalOpen, expandedWorkerId],
  );
  // An open Worker terminal lives in the Workers view, wherever it was opened from.
  const effectiveSideView = workerTerminalOpen ? 'workers' : sideView;
  // The header's failure count opens the Reports list of the same failures.
  const [failuresFocus, setFailuresFocus] = useState(0);
  const openFailures = useCallback(() => {
    setSection('control');
    setWorkerTerminalOpen(false);
    setSideView('reports');
    setFailuresFocus((value) => value + 1);
  }, [setSection]);
  // The header creature's bubble: the same rows the room already holds, no read of its own.
  const watchdogBubbleView = useMemo(() => {
    const last = data.results[0] ?? null;
    return watchdogBubble({
      running: data.running,
      workers: data.workers,
      pendingApprovals: 0,
      plansWaiting: plans.waiting,
      blockedWorkers: 0,
      failedRuns: failures.length,
      outOfTouch: outOfTouchAlerts.length,
      lastFinished: last === null ? null : {
        assignment_id: last.assignment_id, title: runTaskTitle(last, titles), success: last.success,
        worker_id: last.worker_id, worker: workerPhrase(last, data.workers),
      },
    });
  }, [data.running, data.workers, data.results, plans.waiting,
    failures.length, outOfTouchAlerts.length, titles]);
  const openWatchdogLink = useCallback((link: WatchdogLink) => {
    switch (link.kind) {
      case 'worker': expandWorker(link.workerId); return;
      case 'result':
        expandWorker(link.workerId);
        setInspectorTab('work_result');
        setInspectorMode('drawer');
        return;
      case 'reports':
        setSection('control');
        setWorkerTerminalOpen(false);
        setSideView('reports');
        return;
      case 'attention':
        setMainCommandTool('attention');
        setSection('control');
        return;
      case 'plans': setReviewingPlans(true); return;
      case 'failures': openFailures(); return;
      case 'computers':
        setSection('engine');
        setEngineRoomView('live');
        return;
      case 'history':
        setSection('engine');
        setEngineRoomView('history');
        return;
    }
  }, [expandWorker, openFailures, setSection]);
  useEffect(() => {
    if (failuresFocus === 0) return;
    const list = document.getElementById(CURRENT_FAILURES_ID);
    list?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    list?.focus({ preventScroll: true });
  }, [failuresFocus]);
  // #547 canonical configured-vs-actual profiles and runtime capabilities, re-read with the room
  // and (#661) whenever a node says a Worker's Actual AI changed.
  const [aiActualRevision, setAiActualRevision] = useState(0);
  const [aiExecution, setAiExecution] = useState<{
    readonly profiles: readonly WorkerAiExecutionProfileRow[];
    readonly capabilities: readonly AiRuntimeCapabilityRow[];
  }>({ profiles: [], capabilities: [] });
  useEffect(() => {
    let live = true;
    void Promise.all([reader.workerAiExecutionProfiles(orgId), reader.aiRuntimeCapabilities(orgId)]).then(([profiles, capabilities]) => {
      if (!live) return;
      setAiExecution({ profiles: profiles.ok ? profiles.rows : [], capabilities: capabilities.ok ? capabilities.rows : [] });
    });
    return () => { live = false; };
  }, [reader, orgId, data, aiActualRevision]);
  const workerAiProfiles = useMemo<ReadonlyMap<string, AiExecutionProfileView>>(() => {
    const profiles = new Map<string, AiExecutionProfileView>();
    // Runs are read newest first. A Worker with none in view has no run evidence here; the
    // view is bounded, so that is "not yet proven" only together with no live AI on record.
    const latestRunFor = (workerId: string) => {
      const run = data.results.find((row) => row.worker_id === workerId);
      return run === undefined ? null : { runtimeKey: run.runtime_key, modelKey: run.model_key, reportedAt: run.reported_at };
    };
    for (const worker of data.workers) {
      const terminal = data.workerTerminals.find((row) => row.worker_id === worker.id) ?? null;
      const session = data.workforceSessions.find((row) => row.worker_id === worker.id
        && row.status !== 'offline' && (row.lifecycle === 'active' || row.lifecycle === 'restored')) ?? null;
      const nodeId = terminal?.node_id ?? session?.node_id ?? null;
      const executionProfile = aiExecution.profiles.find((row) => row.worker_id === worker.id
        && (nodeId === null || row.node_id === nodeId)) ?? null;
      profiles.set(worker.id, workerAiProfile({
        workerId: worker.id,
        workerNumber: worker.number,
        workerName: worker.name,
        terminal,
        session,
        executionProfile,
        nodes: data.nodes,
        nodeOfferings: data.nodeOfferings,
        offerings: data.modelOfferings,
        proofs: data.runtimeProofs,
        capabilities: aiExecution.capabilities,
        busy: data.running.some((run) => run.worker_number === worker.number),
        latestRun: latestRunFor(worker.id),
      }));
    }
    return profiles;
  }, [data.workers, data.workerTerminals, data.workforceSessions, aiExecution,
    data.nodes, data.nodeOfferings, data.modelOfferings, data.runtimeProofs, data.running, data.results]);
  const overseerProfile = useMemo(() => overseerAiProfile({
    identity: replyIdentities.overseer,
    status: data.plannerHostStatus[0] ?? null,
    nodeOfferings: data.nodeOfferings,
    offerings: data.modelOfferings,
    proofs: data.runtimeProofs,
    capabilities: aiExecution.capabilities,
  }), [replyIdentities, data.plannerHostStatus, data.nodeOfferings, data.modelOfferings,
    data.runtimeProofs, aiExecution.capabilities]);
  // The one structured owner action: set_ai_execution_profile behind the AAL2 guard.
  const applyAiProfile = useCallback(async (
    view: AiExecutionProfileView,
    change: AiExecutionProfileChange,
  ): Promise<AiExecutionProfileApplyResult> => {
    return applyAiProfileChange({
      orgId, view, change, guard,
      rpc: (name, args) => client.rpc(name, args),
      refresh,
    });
  }, [client, guard, orgId, refresh]);
  // #250 the Worker card's AI selector: the same set_worker_offering call and rules as Settings > AI Workforce.
  const workerOffering = useWorkerOffering(client);
  const workerOfferingChoices = useMemo<ReadonlyMap<string, WorkerOfferingChoice>>(() => {
    const choices = new Map<string, WorkerOfferingChoice>();
    for (const worker of data.workers) {
      const choice = workerOfferingChoice({
        worker, sessions: data.workforceSessions, nodes: data.nodes, nodeOfferings: data.nodeOfferings,
        modelOfferings: data.modelOfferings, preferences: data.workerOfferingPreferences,
        runtimeProofs: data.runtimeProofs, nowMs,
      });
      if (choice !== null) choices.set(worker.id, choice);
    }
    return choices;
  }, [data.workers, data.workforceSessions, data.nodes, data.nodeOfferings, data.modelOfferings,
    data.workerOfferingPreferences, data.runtimeProofs, nowMs]);
  const { setOffering: setWorkerOffering } = workerOffering;
  const changeWorkerOffering = useCallback((workerId: string, nodeId: string, offeringKey: string | null) => {
    void guard('post_owner_command', async () => {
      if (await setWorkerOffering(orgId, workerId, nodeId, offeringKey)) await refresh();
    });
  }, [guard, orgId, refresh, setWorkerOffering]);
  const workerMachines = useMemo<readonly CockpitMachineMeta[]>(() => {
    return data.nodes.map((node) => {
      const capacity = data.capacity.find((item) => item.node_id === node.node_id) ?? null;
      const offeringKeys = data.nodeOfferings
        .filter((item) => item.node_id === node.node_id)
        .map((item) => item.offering_key);
      const offerings = [...new Set(offeringKeys.map((key) => {
        const offering = data.modelOfferings.find((item) => item.key === key);
        if (!offering) return key;
        return `${offering.provider_label} · ${offering.model_key}`;
      }))].sort();
      return {
        nodeId: node.node_id,
        name: node.name,
        seats: capacity?.worker_seats ?? null,
        activeRuns: capacity?.active_runs ?? null,
        accepting: capacity?.accepting ?? null,
        holdingBack: capacity?.holding_back ?? null,
        offerings,
      };
    });
  }, [data.nodes, data.capacity, data.nodeOfferings, data.modelOfferings]);
  /** The owner-facing name of each local runtime, from the existing connection catalog. */
  const localRuntimeLabels = useMemo(
    () => Object.fromEntries(AI_CONNECTIONS_DISPLAY.map((row) => [row.connectionKey, row.displayLabel])),
    [],
  );
  const nodeIdByName = useMemo(
    () => new Map(data.nodes.map((node) => [node.name, node.node_id])), [data.nodes]);
  const workerIdByNumber = useMemo(
    () => new Map(data.workers.map((worker) => [worker.number, worker.id])), [data.workers]);
  const nodeIdByWorker = useMemo(
    () => new Map(data.workforceSessions.map((session) => [session.worker_id, session.node_id])),
    [data.workforceSessions]);
  /**
   * One machine-capacity model for the whole screen (#483).
   *
   * Built once, from rows already on the desk, and read by the compact cards in the bottom
   * Machines tab, the Machine Capacity detail in the right drawer, the Resource Health strip
   * and the Core Observer. Nothing re-derives any of it, so the four surfaces cannot disagree
   * about what a computer is under or what it is allowed to do.
   *
   * A live run reaches its machine through the node's own name, and a finished one through
   * the Worker's session, because those are the columns that actually carry the link.
   */
  const machineCapacity = useMemo<readonly MachineCapacity[]>(() => buildMachineCapacity({
    nowMs,
    nodes: data.nodes.map((node) => ({
      node_id: node.node_id, name: node.name, status: node.status, last_seen_at: node.last_seen_at,
    })),
    capacity: data.capacity,
    pressure: data.pressure,
    versions: data.nodeVersions,
    sessions: data.workforceSessions,
    workers: activeWorkers,
    running: data.running.map((row) => ({
      node_id: nodeIdByName.get(row.node_name) ?? null,
      worker_id: row.worker_number === null ? null : workerIdByNumber.get(row.worker_number) ?? null,
      title: row.title,
      started_at: row.started_at,
    })),
    finished: data.results.map((row) => ({
      node_id: row.worker_id === null ? null : nodeIdByWorker.get(row.worker_id) ?? null,
      reported_at: row.reported_at,
      success: row.success,
    })),
    localRuntimes: data.aiLocalRuntimeStatuses,
    runtimeLabels: localRuntimeLabels,
    samples: data.resourceSamples,
    contract: data.capacityContract ?? DEFAULT_CAPACITY_GUARD_CONTRACT,
    available: {
      capacity: observerReads.capacity,
      pressure: observerReads.pressure,
      samples: observerReads.resourceSamples,
    },
  }), [
    nowMs, data.nodes, data.capacity, data.pressure, data.nodeVersions, data.workforceSessions,
    activeWorkers, data.running, data.results, data.aiLocalRuntimeStatuses, data.resourceSamples,
    data.capacityContract, observerReads.capacity, observerReads.pressure, observerReads.resourceSamples,
    nodeIdByName, workerIdByNumber, nodeIdByWorker, localRuntimeLabels,
  ]);

  /** Every recorded change to one machine's capacity limits, newest first. */
  const machineLimitChanges = useMemo(
    () => data.audit.filter((event) => event.event_type === 'node.limits_set'
      && (event.payload as { nodeId?: unknown } | null)?.nodeId === selectedMachineId),
    [data.audit, selectedMachineId],
  );

  const aiCapacityMeters = useMemo(
    () => cockpitAiMeters(data.providerUsage, data.outages, data.aiUsagePolicy, nowMs, {
      connections: data.aiConnectionStatuses,
      localStatuses: data.aiLocalRuntimeStatuses,
      nodes: data.nodes,
      pressure: data.pressure,
      capacity: data.capacity,
      nodeOfferings: data.nodeOfferings,
      modelOfferings: data.modelOfferings,
    }),
    [
      data.providerUsage, data.outages, data.aiUsagePolicy, nowMs,
      data.aiConnectionStatuses, data.aiLocalRuntimeStatuses, data.nodes,
      data.pressure, data.capacity, data.nodeOfferings, data.modelOfferings,
    ],
  );
  const aiUsageAdvisories = useMemo(
    () => cockpitAiAdvisories(aiCapacityMeters, data.aiUsagePolicy),
    [aiCapacityMeters, data.aiUsagePolicy],
  );
  const workerAttentions = useMemo(() => cockpitWorkerAttentions(data.workerTerminals), [data.workerTerminals]);
  const liveEvents = useMemo(() => cockpitLiveStatusEvents({
    plans: data.commandPlans, workers: data.workerTerminals, nodesOutOfTouch: outOfTouch, audit: data.audit,
  }), [data.commandPlans, data.workerTerminals, outOfTouch, data.audit]);
  const attentionCount = cockpitAttentionCount({
    pendingApprovals: 0, plansWaiting: plans.waiting, workerAttentions,
    ownerGates: ownerGateWaiting.waiting.length,
  });

  /*
   * The Engine Room reads the same rows the rest of the desk already holds.
   * Machines arrive through one adapter, lib/engine-room-machines, which is the
   * only place on that page that touches the machine capacity/limits contract;
   * everything else - the captain status, the work section, the Machinist -
   * reads the adapter's shape. The adapter is handed the one #483 machine
   * capacity model built above, so the Engine Room, the bottom Machines cards
   * and the Machine Capacity drawer cannot disagree about a machine. Nothing
   * here polls, writes or schedules.
   */
  const engineMachines = useMemo(() => engineRoomMachines({
    nodes: data.nodes,
    capacity: data.capacity,
    pressure: data.pressure,
    versions: data.nodeVersions,
    localRuntimes: data.aiLocalRuntimeStatuses,
    nodeOfferings: data.nodeOfferings,
    modelOfferings: data.modelOfferings,
    running: data.running,
    results: data.results,
    machineCapacity,
    nowMs,
  }), [
    data.nodes, data.capacity, data.pressure, data.nodeVersions, data.aiLocalRuntimeStatuses,
    data.nodeOfferings, data.modelOfferings, data.running, data.results, machineCapacity, nowMs,
  ]);
  // The same projection the Work Board draws, read again rather than re-derived:
  // the Engine Room summarises work, it does not hold a second board.
  const engineWorkBoard = useMemo(() => buildWorkBoard({
    orgId,
    roadmap: roadmapItems,
    plans: [...data.planHistory, ...data.plans],
    missionTasks: data.missionTasks,
    running: data.running,
    results: data.results,
    commandPlans: data.commandPlans,
    workerTerminals: data.workerTerminals,
    approvals: [],
    workers: data.workers,
    projects: data.projects,
    capacity: data.capacity,
    nowMs,
    jobRecoveries: data.jobRecoveries,
  }), [
    orgId, roadmapItems, data.planHistory, data.plans, data.missionTasks, data.running,
    data.results, data.commandPlans, data.workerTerminals, data.workers,
    data.projects, data.capacity, nowMs, data.jobRecoveries,
  ]);
  const engineRoomModel = useMemo(() => buildEngineRoom({
    machines: engineMachines,
    meters: aiCapacityMeters,
    systems,
    workBoard: engineWorkBoard,
    running: data.running,
    nowMs,
  }), [engineMachines, aiCapacityMeters, systems, engineWorkBoard, data.running, nowMs]);
  // Deterministic, and only deterministic. No model is asked anything to draw this.
  const machinistReport: MachinistReport = useMemo(() => runMachinist(engineRoomModel), [engineRoomModel]);

  const badges = sectionBadges({
    proposedPlans: plans.total,
    pendingApprovals: 0,
    blockedWorkers: 0,
    nodesOutOfTouch: outOfTouchAlerts.length,
    engineRoomAlerts: engineRoomModel.captain.infrastructureIssues + engineRoomModel.captain.aiCapacityIssues,
  });

  /** The AI Usage drawer, already built, opened on one lane. */
  const openAiUsageDetail = useCallback((key: string) => {
    setSelectedUsageKey(key);
    setInspectorTab('usage');
    setInspectorMode('drawer');
    setUsageFocusRequest((value) => value + 1);
  }, []);
  /** The Connector Center, already built, opened on one system. */
  const openSystemDetail = useCallback((typeKey: SystemConnectorType) => {
    setSelectedSystemType(typeKey);
    setInspectorTab('systems');
    setInspectorMode('drawer');
  }, []);
  /** The Work Board, already built and still the only one. */
  const openWorkBoardDetail = useCallback(() => {
    setInspectorTab('workboard');
    setInspectorMode('drawer');
  }, []);
  const openRoadmapItem = useCallback((itemId: string) => {
    setRoadmapFocusId(itemId);
    setInspectorTab('roadmap');
    setInspectorMode('drawer');
  }, []);
  /** The existing Machine Capacity drawer (#483) on one computer: its readings, guards and limits. */
  const openMachineCapacity = useCallback((nodeId: string) => {
    setSelectedMachineId(nodeId);
    setInspectorTab('machines');
    setInspectorMode('drawer');
  }, []);
  /** Which computer's Machine Board is open, kept in the address as #machine/<node> (#492). */
  const machineBoard = useMachineBoardLink();
  /**
   * One way into a computer's own detail, shared by the bottom Machines cards,
   * the Engine Room's machine cards and the Machinist's machine findings. It
   * opens that computer's Machine Board (#492) under the Engine Room; the
   * board's own "Machine limits" link opens the capacity drawer above.
   */
  const openMachineDetail = useCallback((nodeId: string) => {
    machineBoard.open(nodeId);
    setSection('engine');
  }, [machineBoard.open]);
  // A bookmarked board link lands on the Engine Room, where the board lives.
  useEffect(() => {
    if (machineBoard.nodeId !== null) setSection('engine');
  }, [machineBoard.nodeId]);
  /**
   * The Machinist reports upward through the one Command Center conversation.
   * It fills the command box and stops: the owner reads it, edits it if he
   * likes, and decides whether to send it. Nothing is dispatched from here.
   */
  const reportMachinistToMainCommand = useCallback(() => {
    setNoteHandoff(null);
    setDraft({
      text: formatMachinistForMainCommand(
        machinistOverseerReport(machinistReport, new Date(nowMs).toISOString()),
      ),
      at: Date.now(),
    });
    setSection('control');
  }, [machinistReport, nowMs]);

  // Phase 1 sound notifications: react to Worker status / permission transitions.
  // First snapshot is silent (no fanfare on load). Visual notices stay independent.
  useEffect(() => {
    const player = getSharedNotificationPlayer();
    const unlock = () => player.unlock();
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);

    const terminalNeeds = new Map<string, boolean>();
    for (const row of data.workerTerminals) {
      const needs = row.terminal_status === 'waiting_for_approval' || row.terminal_status === 'waiting_for_input';
      terminalNeeds.set(row.worker_id, needs);
    }
    const next = buildNotificationSnapshots(
      activeWorkers.map((w) => ({ id: w.id, number: w.number, status: w.status })),
      NO_PENDING_APPROVALS,
      terminalNeeds,
    );
    const previous = notificationSnapshots.current;
    notificationSnapshots.current = next;
    void dispatchNotificationTransitions(player, previous, next);
    void player.ensureLoaded();

    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [activeWorkers, data.workerTerminals]);

  const liveDockActivity = useMemo(() => data.reports.slice(0, 12).map((report) => ({
    id: report.id,
    label: report.envelope.reportKind ?? 'Worker report',
    detail: report.envelope.summary ?? null,
    at: report.created_at,
  })), [data.reports]);
  const liveDockMachines = useMemo(() => data.nodes.map((node) => {
    const capacity = data.capacity.find((item) => item.node_id === node.node_id) ?? null;
    const state = connectionFromPresenceRead({
      lastSeenAt: node.last_seen_at,
      status: node.status as OperationalStatus,
      presenceReadAtMs,
    });
    const seatDetail = capacity?.worker_seats === null || capacity?.worker_seats === undefined
      ? null
      : `${capacity.active_runs ?? 0}/${capacity.worker_seats} active seats`;
    const pressure = capacity?.accepting === false
      ? `holding: ${capacity.holding_back?.replaceAll('_', ' ') ?? 'capacity'}`
      : capacity?.accepting === true ? 'room available' : null;
    return {
      id: node.node_id,
      name: node.name,
      status: state,
      detail: [seatDetail, pressure].filter((part): part is string => part !== null).join(' · ') || null,
      capacity: machineCapacity.find((machine) => machine.nodeId === node.node_id) ?? null,
    };
  }), [data.nodes, data.capacity, machineCapacity, presenceReadAtMs]);


  /**
   * The owner takes the keyboard back through the path that already exists: releasing the
   * input hold on that terminal. Nothing is closed, nothing is restarted, and the Worker
   * carries on - what changes is only who may type next.
   */
  const takeKeyboard = (workerId: string, sessionId: string | null) => {
    expandWorker(workerId);
    if (sessionId === null) return;
    void guard('post_owner_command', async () => {
      await terminalControl.release(orgId, sessionId, null, 'the owner took the keyboard');
      await refreshCockpit();
    });
  };

  // The Workers as routing knows them - a number, a name, and the computer the owner calls
  // it by - and the standing instruction for the project he is working in.
  const routingWorkers = useMemo(() => data.workerTerminals.map((row) => ({
    number: row.worker_number, name: row.worker_name, nodeName: row.node_name,
  })), [data.workerTerminals]);
  const activeRouting = data.workerRouting.find((row) => row.project_id === projectId) ?? null;
  const routingLock = routingLockLabel(activeRouting?.worker_number ?? null, routingWorkers);

  /** Back to any idle Worker. The owner's own hand, through the same guarded call. */
  const clearRoutingLock = () => {
    if (projectId === '') return;
    void guard('post_owner_command', async () => {
      if (await routingScope.set(orgId, projectId, null)) await refreshCockpit();
    });
  };

  /** One Worker's expanded surface. Collapsing it hides this; it never unmounts it. */
  const renderWorkerSurface = (workerId: string, active: boolean) => {
    const worker = data.workers.find((item) => item.id === workerId) ?? null;
    if (worker === null) return null;
    const currentRun = data.running.find((run) => run.worker_number === worker.number) ?? null;
    const offer = discardOffer(workerChatState.find((item) => item.workerId === worker.id) ?? null, { running: data.running });
    const discardNodeId = offer.nodeId;
    return (
      <WorkerSurface
        client={client} guard={guard} orgId={orgId} worker={worker} active={active}
        session={data.workforceSessions.find((session) => session.worker_id === worker.id
          && session.status !== 'offline' && (session.lifecycle === 'active' || session.lifecycle === 'restored')) ?? null}
        reader={reader} onChanged={refresh}
        currentRun={currentRun}
        currentProgress={latestProgressForRun(currentRun, data.progress)}
        terminalDebug={workerTerminalDebug}
        terminalWaiting={workerCards.some((card) => card.workerId === worker.id && card.terminalWaiting === true)}
        aiProfile={workerAiProfiles.get(worker.id) ?? null}
        onApplyAiProfile={applyAiProfile}
        onCollapse={closeWorkerTerminal}
        onDiscard={offer.offered && discardNodeId !== null
          ? (onResult) => discardWorker({
            orgId, workerId: worker.id, workerNumber: worker.number, nodeId: discardNodeId,
            running: data.running, routing: data.workerRouting,
            guard, rpc: (name, args) => client.rpc(name, args), refresh, onResult,
          })
          : null}
      />
    );
  };

  // A question is answered here, from the rows already on the screen: who is
  // working, what is running, what is waiting, which computers are in touch.
  // No model call - the desk already knows, and a computed answer cannot be
  // wrong about the state it is reading.
  // #644 Slice D: the one canonical workforce snapshot, re-read with the room (isolated from
  // the room loaders). Workforce questions are answered from it and nothing else.
  const [workforce, setWorkforce] = useState<WorkforceSnapshot | null>(null);
  useEffect(() => {
    let live = true;
    void reader.workforceSnapshot(orgId).then((snapshot) => {
      if (live) setWorkforce(snapshot);
    });
    return () => { live = false; };
  }, [reader, orgId, data]);

  const answers = useMemo(() => {
    const facts = {
      workers: activeWorkers, running: data.running,
      approvalsWaiting: 0, plansWaiting: plans.waiting,
      ownerGates: ownerGateWaiting.waiting,
      nodes: data.nodes, nodesConnected: data.nodes.length - outOfTouch.length,
      workforce,
      roadmap: roadmapReadOk ? roadmapItems : null,
    };
    const map = new Map<string, string>();
    for (const command of data.commands) {
      const answer = answerFor(command.content, facts);
      if (answer !== null) map.set(command.id, answer);
    }
    return map;
  }, [data.commands, activeWorkers, data.running, data.nodes, plans.waiting, ownerGateWaiting.waiting, outOfTouch.length, workforce, roadmapItems, roadmapReadOk]);

  // The desk's transcript: every command the owner sent and what came back of
  // it. Plan history holds every plan whatever was decided; the plans still
  // waiting are added for the ones history has not caught up with.
  const commandsWithWorkerTargets = useMemo(() => data.commands.map((command) => ({
    ...command,
    worker_reply_targets: data.workerRecipients
      .filter((recipient) => recipient.owner_command_id === command.id)
      .map((recipient) => recipient.worker_id),
  })), [data.commands, data.workerRecipients]);

  const everyPlan = useMemo(() => {
    const seen = new Set(data.planHistory.map((p) => p.id));
    return [...data.planHistory, ...data.plans.filter((p) => !seen.has(p.id))];
  }, [data.planHistory, data.plans]);
  // #561: copy-ready requests per approved plan from canonical plans + readiness. Copy only.
  const workerRequestBlocks = useMemo(() => {
    const workers = workerRequestFactsFromReadiness(workerChatState);
    const byPlan = new Map<string, readonly WorkerRequestBlock[]>();
    for (const plan of everyPlan) {
      const projectName = data.projects.find((project) => project.id === plan.project_id)?.name ?? null;
      const blocks = approvedPlanWorkerRequestBlocks(plan, {
        repository: projectName === null ? 'Unknown' : `Unknown (project: ${projectName})`,
        workers,
      });
      if (blocks.length > 0) byPlan.set(plan.id, blocks);
    }
    return byPlan;
  }, [everyPlan, data.projects, workerChatState]);

  // #644 Slice C control receipts, re-read with the room (isolated from the room loaders).
  const [commandControls, setCommandControls] = useState<ReadonlyMap<string, OwnerCommandControl>>(new Map());
  useEffect(() => {
    let live = true;
    void reader.ownerCommandControls(orgId).then((result) => {
      if (!live) return;
      setCommandControls(new Map(result.ok ? result.rows.map((row) => [row.command_id, row]) : []));
    });
    return () => { live = false; };
  }, [reader, orgId, data]);

  // #743: which earlier message each owner message replied to, from the canonical rows.
  useEffect(() => {
    let live = true;
    void reader.ownerCommandReplyRefs(orgId).then((result) => {
      if (!live || !result.ok) return;
      setCommandReplyRefs((current) => {
        const next = new Map(current);
        for (const row of result.rows) {
          const ref = parseReplyRef(row.reply_to);
          if (ref !== null) next.set(row.id, ref);
        }
        return next;
      });
    });
    return () => { live = false; };
  }, [reader, orgId, data]);

  const thread = useMemo(() => {
    // Approving is not the end of the thread: what the work came to is said in
    // the turn it was approved in, so the owner never has to go and look.
    return commandThread(commandsWithWorkerTargets, everyPlan, nowMs, answers, {
      context: {
        projects: data.projects, workers: data.workers, running: data.running,
        routes: planRouteTargets, scopes: data.workerRouting, seats: planSeats,
      },
      outcomes: planOutcomes(everyPlan, data.missionTasks, data.results, data.running, data.workers, data.audit, data.jobRecoveries),
      controls: commandControls,
      generated: new Set<string>(),
      overseerReplies: overseerConversationCommands({
        observerMessages: data.observerMessages,
        deliveries: [...mainCommandDeliveries.values(), ...acceptedMainCommands],
      }),
      archivedPlans: proposalArchiveResult.archived,
    });
  }, [commandsWithWorkerTargets, everyPlan, data.projects, data.workers, data.running, planRouteTargets, planSeats, data.workerRouting,
    data.missionTasks, data.results, data.audit, data.jobRecoveries, nowMs, answers, commandControls,
    data.observerMessages, mainCommandDeliveries, acceptedMainCommands, proposalArchiveResult]);

  const displayThread = useMemo(() => {
    const pending = [...unreconciledMainCommandEchoes(data.commands, acceptedMainCommands)]
      .sort((a, b) => (a.seq ?? Number.MAX_SAFE_INTEGER) - (b.seq ?? Number.MAX_SAFE_INTEGER))
      .map((accepted) => ({
        id: accepted.commandId,
        kind: 'command' as const,
        mine: true,
        text: accepted.text,
        seq: accepted.seq,
        status: null,
        reason: null,
        tasks: null,
        atLabel: '',
        offer: null,
        outcome: null,
        statusLabel: null,
        replyTargets: accepted.targets,
        workerTargets: accepted.workerIds,
      }));
    return [...thread, ...pending];
  }, [thread, data.commands, acceptedMainCommands]);

  const recipientStatusesByCommand = useMemo(() => {
    const acceptedByCommand = new Map(acceptedMainCommands.map((accepted) => [accepted.commandId, accepted]));
    const statuses = new Map<string, ReturnType<typeof mainCommandRecipientStatuses>>();
    for (const turn of displayThread) {
      if (!turn.mine) continue;
      const accepted = acceptedByCommand.get(turn.id);
      const delivery = mainCommandDeliveries.get(turn.id);
      // Do not invent waiting/offline truth for older rows outside the bounded
      // canonical hydration window.
      if (accepted === undefined && delivery === undefined) continue;
      statuses.set(turn.id, mainCommandRecipientStatuses({
        commandId: turn.id,
        targets: (turn.replyTargets ?? ['overseer']).filter((target): target is MainCommandReplyTarget =>
          target === 'overseer' || target === 'ux' || target === 'core'),
        workerIds: turn.workerTargets ?? [],
        workerLabels,
        managementDeliveries: delivery?.managementDeliveries ?? accepted?.managementDeliveries,
        workerDeliveries: delivery?.workerDeliveries ?? accepted?.workerDeliveries,
        plans: everyPlan,
        observerMessages: data.observerMessages,
        workerMessages: data.workerChatMessages,
      }));
    }
    return statuses;
  }, [acceptedMainCommands, displayThread, mainCommandDeliveries, workerLabels, everyPlan, data.observerMessages, data.workerChatMessages]);

  useEffect(() => {
    setCommandReceiptBook((current) => {
      if (current.byCommand.size === 0) return current;
      let changed = false;
      const byCommand = new Map(current.byCommand);
      for (const [commandId, view] of current.byCommand) {
        const control = commandControls.get(commandId) ?? null;
        const statuses = recipientStatusesByCommand.get(commandId) ?? [];
        const next = receiptForCommandAction({
          control: control === null ? null : { state: control.state, reason: control.reason, summary: control.summary },
          deliveries: statuses.map((status) => ({
            state: status.state,
            reason: status.state === 'failed' ? failedDeliveryReason(status.text) : null,
          })),
        });
        const advanced = advanceReceipt(view, next);
        if (!sameReceipt(advanced, view)) {
          byCommand.set(commandId, advanced);
          changed = true;
        }
      }
      return changed ? { ...current, byCommand } : current;
    });
  }, [commandControls, recipientStatusesByCommand]);

  const managementAttentionCount = plans.total + ownerGateWaiting.waiting.length;

  // #709: one "Overseer needs your attention" per refresh that adds a genuinely new owner need.
  // Nothing sounds until the first real snapshot has loaded; acknowledging never resolves anything.
  useEffect(() => {
    if (data === EMPTY) return;
    const next = ownerNeededKeys({ plans: attentionPlans });
    const added = ownerNeededAdded(ownerNeededSnapshot.current, next);
    ownerNeededSnapshot.current = next;
    if (added.length === 0) return;
    const player = getSharedNotificationPlayer();
    void player.enqueuePlan(ownerNeededPlaybackPlan(player.getSettings()), `owner_needed:${added.join(',')}`, 'Owner needed');
  }, [data, attentionPlans]);

  const sharedTimeline = useMemo(() => {
    const openThreadIds = new Set(
      data.observerThreads
        .filter((row) => row.status === 'open' || row.status === 'approved' || row.status === 'prototype')
        .map((row) => row.id),
    );
    return buildSharedCommandTimeline({
      commandTurns: displayThread,
      observerMessages: data.observerMessages,
      workerMessages: data.workerChatMessages,
      workerLabels,
      discussion: chatMode,
      openThreadIds,
      nowMs,
      recipientStatusesByCommand,
    });
  }, [displayThread, data.observerMessages, data.workerChatMessages, data.observerThreads, workerLabels, chatMode, nowMs, recipientStatusesByCommand]);

  const mainCommandConversation = useMainCommandConversationController({
    turns: displayThread,
    client,
    guard,
    conversationKey: orgId,
    timeline: sharedTimeline,
    discussion: chatMode,
  });
  const floatingConversation = activeMiniConversation(
    mainCommandConversation.recentChats,
    mainCommandConversation.viewingArchiveId,
  );

  const resolveComposerRecipients = useCallback((content: string) => resolveMainCommandMentions({
    content,
    currentTargets: [...replyTargets],
    currentWorkerIds: [...workerReplyTargets],
    candidates: mentionCandidates,
  }), [replyTargets, workerReplyTargets, mentionCandidates]);

  const mainCommandComposer = useMainCommandComposerController({
    client,
    guard,
    orgId,
    projectId,
    draft,
    onSent: async () => {
      setNoteHandoff(null);
      setReplyingTo(null);
      await refresh();
    },
    plannerUrl,
    discussion: chatMode,
    replyTargets: [...replyTargets],
    workerReplyTargets: [...workerReplyTargets],
    observerThreadId: null,
    onBeforeSend: markCommandSent,
    onAccepted: (accepted) => {
      bindCommandReceipt(accepted);
      onMainCommandAccepted(accepted);
      if (replyingTo !== null) {
        const ref = replyingTo.ref;
        setCommandReplyRefs((current) => new Map(current).set(accepted.commandId, ref));
      }
    },
    resolveRecipients: resolveComposerRecipients,
    replyTo: replyingTo?.ref ?? null,
  });

  useEffect(() => {
    const message = mainCommandComposer.sendError;
    if (message === null || message.trim() === '') return;
    setCommandReceiptBook((current) => {
      if (current.pending === null || current.pending.refusedReason !== null
        || current.pending.acknowledged || current.pending.done) return current;
      return { ...current, pending: receiptFromSubmitRefusal(message) };
    });
  }, [mainCommandComposer.sendError]);

  // #856: an update never auto-reloads over an unsent Main Command. "Reload now" carries it
  // across, except mid-send: a draft restored after the server took it would invite a resend.
  // The draft is read when a reload asks, not on every render: see ComposerDraftStore.
  const mainCommandReloadState = () => ({
    orgId, content: mainCommandComposer.getContent(), replyTargets: [...replyTargets],
    workerReplyTargets: [...workerReplyTargets], replyingTo, noteHandoff, chatMode,
  });
  useReloadCheck(() => {
    if (mainCommandComposer.sending) return { reason: 'Main Command is sending', preserved: false };
    return mainCommandHasUnsent(mainCommandReloadState())
      ? { reason: 'Main Command has an unsent message or reply', preserved: true }
      : null;
  });
  useReloadPreserver(() => {
    if (mainCommandComposer.sending) return false;
    return saveMainCommandForReload(sessionStore(), mainCommandReloadState());
  });

  const getMainCommandDraft = mainCommandComposer.getContent;
  const directReply = useCallback((recipient: MainCommandDirectRecipient) => {
    const selection = directReplySelection(recipient);
    setReplyTargets(new Set(selection.targets));
    setWorkerReplyTargets(new Set(selection.workerIds));
    setDraft({ text: getMainCommandDraft(), at: Date.now() });
  }, [getMainCommandDraft]);

  // #743: Reply on one exact message: quote it above the composer and, for a participant's
  // message, reply to that participant. The owner can still change the recipients.
  const replyToMessage = useCallback((item: SharedTimelineItem) => {
    const draftReply = replyDraftForItem(item);
    if (draftReply === null) return;
    setReplyingTo(draftReply);
    if (item.replyRecipient) directReply(item.replyRecipient);
    else setDraft({ text: getMainCommandDraft(), at: Date.now() });
  }, [directReply, getMainCommandDraft]);

  // Every reply on the desk, whichever side it came from: a plan, an answer, or a
  // note, or a worker's report. The reading itself only cares that it is not
  // the owner's own words coming back at them.
  const replies = useMemo(() => [
    ...thread.filter((turn) => !turn.mine).map((turn) => ({ id: turn.id, text: turn.text })),
    ...data.reports.map((r) => ({ id: r.id, text: `${r.envelope.reportKind ?? 'Report'}: ${r.envelope.summary ?? ''}` })),
  ], [thread, data.reports]);

  // Talk back: read each reply that arrives in the conversation. What is on
  // the screen when the desk opens is not read - the owner has not missed it.
  useEffect(() => {
    if (spokenTurns.current === null) { spokenTurns.current = new Set(replies.map((r) => r.id)); return; }
    const fresh = unspoken(replies, spokenTurns.current);
    replies.forEach((reply) => spokenTurns.current!.add(reply.id));
    if (voiceReplies && fresh.length > 0) speakText(speechFor(fresh));
  }, [replies, voiceReplies]);

  // Switching it on says the last reply straight away, on the click itself, so
  // the owner hears that it is on rather than waiting for the next one to find
  // out - and a browser that only speaks for a gesture has one to speak for.
  const onVoiceReplies = (on: boolean) => {
    setVoiceReplies(on);
    if (!on) { stopSpeaking(); return; }
    const last = [...replies].reverse().find((reply) => reply.text.trim() !== '');
    speakText(last ? last.text : 'Reading replies aloud. Nothing has come back yet.');
  };


  const coreTerminalNodeKey = useMemo(() => [...new Set(
    data.workerTerminals
      .map((row) => row.node_id)
      .filter((nodeId): nodeId is string => nodeId !== null),
  )].sort().join(','), [data.workerTerminals]);

  // Listen on the existing shared node-scoped terminal Realtime channel. This adds
  // no PTY and no second transport: subscribeTerminalRealtime fans the same
  // channel out locally to the Worker surfaces and the live dock.
  useEffect(() => {
    if (coreTerminalNodeKey === '') return undefined;
    const nodeIds = coreTerminalNodeKey.split(',');
    const leases = nodeIds.map((nodeId) =>
      subscribeTerminalRealtime(client, nodeId, {
        onTransport: (state) => {
          terminalTransports.current.set(nodeId, state);
          const stopped = liveDockChannelStopped([...terminalTransports.current.values()]);
          channelStoppedRef.current = stopped;
          setChannelClosedOrErrored((current) => (current === stopped ? current : stopped));
          if (!stopped) setLastFreshAt(lastFreshAtRef.current);
        },
        onBroadcast: (event, payload) => {
          const at = Date.now();
          lastFreshAtRef.current = at;
          setLastFreshAt((current) => {
            if (channelStoppedRef.current) return current;
            if (current === null || at - current > LIVE_DOCK_FRESHNESS_ALLOWANCE_MS) return at;
            return current;
          });
          if (event === 'ai-execution-actual') {
            setAiActualRevision((value) => value + 1);
            return;
          }
          if (event === 'persistent-ai-state') {
            const raw = payload as Record<string, unknown>;
            if (typeof raw['workerId'] !== 'string'
              || typeof raw['sessionId'] !== 'string'
              || typeof raw['runtimeKey'] !== 'string'
              || typeof raw['state'] !== 'string') return;
            const snapshot: PersistentAiStateSnapshot = {
              workerId: raw['workerId'],
              sessionId: raw['sessionId'],
              runtimeKey: raw['runtimeKey'],
              modelKey: typeof raw['modelKey'] === 'string' ? raw['modelKey'] : null,
              state: raw['state'],
              processId: typeof raw['processId'] === 'string' ? raw['processId'] : null,
              taskId: typeof raw['taskId'] === 'string' ? raw['taskId'] : null,
              runId: typeof raw['runId'] === 'string' ? raw['runId'] : null,
              durableResultAt: typeof raw['durableResultAt'] === 'number' ? raw['durableResultAt'] : null,
              lastActivityAt: typeof raw['lastActivityAt'] === 'number' ? raw['lastActivityAt'] : null,
              supported: raw['supported'] !== false,
              unsupportedReason: typeof raw['unsupportedReason'] === 'string' ? raw['unsupportedReason'] : null,
            };
            setPersistentWorkerSessions((current) => [
              snapshot,
              ...current.filter((item) => item.workerId !== snapshot.workerId),
            ]);
            setWorkerChatHeardNodes((current) => {
              if (current.has(nodeId)) return current;
              const next = new Set(current);
              next.add(nodeId);
              return next;
            });
            return;
          }
        },
      }),
    );
    return () => {
      for (const lease of leases) lease.release();
      for (const nodeId of nodeIds) terminalTransports.current.delete(nodeId);
      const stopped = liveDockChannelStopped([...terminalTransports.current.values()]);
      channelStoppedRef.current = stopped;
      setChannelClosedOrErrored((current) => (current === stopped ? current : stopped));
    };
  }, [client, coreTerminalNodeKey]);

  return (
    <div className={railPinned ? 'shell rail-pinned' : 'shell'} data-section={section} ref={shell}>
      <header className="topbar" ref={topbar}>
        <div className="tb-left">
          <button type="button" className={railPinned ? 'ghost icon on' : 'ghost icon'} aria-pressed={railPinned}
            title="Menu - keep the sections open" aria-label="Menu" onClick={togglePin}>☰</button>
          <Brand />
          <span className="app-version" title={`Build ${brunelBuildId()}`}>{brunelVersionLabel()}</span>
          <ContextSwitcher
            orgs={orgs}
            orgId={orgId}
            projects={data.projects}
            projectId={projectId}
            onOrg={selectOrganization}
            onProject={setProjectId}
          />
        </div>
        {/* The creature crawls along the bar's own edge. It is decoration:
            every state it shows is written beside it in words, and the rail's
            badges and the panels say the same thing again. */}
        <Watchdog
          connectedNodes={data.nodes.length - outOfTouch.length}
          totalNodes={data.nodes.length - (outOfTouch.length - outOfTouchAlerts.length)}
          running={data.running.length}
          pendingApprovals={plans.waiting}
          blockedWorkers={0}
          failedRuns={failures.length}
          onOpenFailures={openFailures}
          bubble={watchdogBubbleView}
          onOpen={openWatchdogLink}
        />
        <div className="topbar-right">
          {!owner.verified && (
            <button type="button" className="attention-chip" onClick={owner.verify}
              title="Verify once now and Brunel stops asking for the rest of this sign-in">
              Verify owner
            </button>
          )}
          <ModeToggle />
        </div>
      </header>

      <div className={[
        'body',
        liveDockExpanded ? 'live-dock-open' : '',
        inspectorVisibleMode === 'docked' ? 'inspector-docked' : '',
        railLabels ? 'rail-labels-open' : '',
      ].filter(Boolean).join(' ')}
        style={{
          '--inspector-rail-width': `${inspectorRailPx}px`,
          ...(inspectorVisibleMode === 'docked'
            ? { '--inspector-width': `${inspectorDisplayWidth}px` }
            : {}),
        } as React.CSSProperties}>
        <Rail
          section={section}
          badges={badges}
          onSection={setSection}
          onHelp={() => setWorkflowGuideOpen(true)}
          supportHref={brunelSupportUrl({
            version: brunelAppVersion(), buildId: brunelBuildId(), surface: section, production: import.meta.env.PROD,
          })}
        />

        <div className="workzone">
          {/* The divider's width is handed to the sheet as a custom property rather than as
              a whole grid-template. An inline template would outrank every rule in the
              stylesheet, including the one that folds the Cockpit into a single column on a
              narrow screen; a property is a value the sheet can still decide what to do
              with, so the fifty-fifty default and the narrow fallback both survive. */}
          {section === 'control' && (
            <main className="work cockpit" aria-label="Control Center" ref={desk}
              style={sideWidth === null ? undefined : ({ '--cockpit-side': `${sideWidth}px` } as React.CSSProperties)}>
              <div className="work-main">
                {/* One global command surface stays on the LEFT. Questions, work and plan
                    changes begin here; direct intervention remains inside each Worker card. */}
                <section className="cockpit-command" aria-label="Command Center">
                  <div className="cockpit-command-content">
                  <CommandChat turns={displayThread} client={client} guard={guard} onDecided={refresh}
                    action={data === EMPTY ? undefined : (
                      <span className="autopilot-heading">
                        <AutopilotHeartbeatStatus read={autopilotHeartbeat} nowMs={nowMs} />
                      </span>
                    )}
                    workerRequestBlocks={workerRequestBlocks}
                    onDirectReply={directReply}
                    onReplyToMessage={replyToMessage}
                    replyRefs={commandReplyRefs}
                    controlReceipts={commandReceiptBook.byCommand}
                    controlReceiptActions={commandReceiptActions}
                    conversationController={mainCommandConversation}
                    organizerTool={mainCommandTool}
                    onOrganizerToolChange={setMainCommandTool}
                    attentionCount={managementAttentionCount}
                    managementAttention={(
                      <ManagementAttention
                        ownerGates={ownerGateWaiting.waiting}
                        planCount={plans.total}
                        archivedCount={proposalArchiveResult.rows.length}
                        onReviewPlans={() => setReviewingPlans(true)}
                      />
                    )}
                    recipientBar={(
                      <div className="stack tight">
                        <ReplyTargetBar
                          selected={replyTargets}
                          identities={replyIdentities}
                          onToggle={toggleReplyTarget}
                          workers={workerChatState}
                          eligibleWorkerIds={eligibleWorkerIds}
                          selectedWorkers={workerReplyTargets}
                          onToggleWorker={toggleWorkerReplyTarget}
                          onToggleAllWorkers={toggleAllWorkerReplyTargets}
                          overseerProfile={overseerProfile}
                          onApplyOverseerProfile={(change) => applyAiProfile(overseerProfile, change)}
                        />
                      </div>
                    )}
                    >
                    <CommandComposer
                      client={client}
                      guard={guard}
                      orgId={orgId}
                      controller={mainCommandComposer}
                      voiceReplies={voiceReplies}
                      onVoiceReplies={onVoiceReplies}
                      nodeConnection={nodeConnection}
                      height={composerHeight}
                      onHeight={setComposerHeight}
                      mentionCandidates={mentionCandidates}
                      onMentionSelect={selectMentionRecipient}
                      noteHandoff={noteHandoff}
                      replyingTo={replyingTo}
                      onCancelReply={() => setReplyingTo(null)}
                      onDismissNoteHandoff={() => {
                        const unsent = mainCommandComposer.getContent();
                        if (noteHandoff !== null && (
                          unsent === noteHandoff.text
                          || unsent.trim() === ''
                        )) {
                          setDraft({ text: '', at: Date.now() });
                        }
                        setNoteHandoff(null);
                      }}
                      projectId={projectId}
                      workerIds={[...workerReplyTargets]}
                      projects={data.projects}
                      workers={data.workers.filter((worker) => !worker.retired_at)}
                      commandReceipt={commandReceiptBook.pending}
                    />
                  </CommandChat>

                  {routingLock !== null && (
                    <p className="row spread wrap cockpit-routing">
                      <span className="row">
                        <span className="tag">Routing</span>
                        <strong>{routingLock}</strong>
                      </span>
                      <button type="button" className="link" disabled={routingScope.busy}
                        onClick={clearRoutingLock}>Use any Worker</button>
                    </p>
                  )}
                  {routingScope.error !== null && <p role="alert">{routingScope.error}</p>}
                  </div>
                </section>

                {workerAttentions.length > 0 && (
                  <section className="cockpit-attention stack" aria-label="Needs you">
                    {workerAttentions.map((item) => (
                      <OwnerAttentionPanel key={item.workerId} kind={item.kind} title={item.title}
                        workerLabel={item.workerLabel} outcomeLabel={item.outcomeLabel}
                        explanation={item.explanation}
                        indicator={<WorkerStatusIndicator status={workerStatusOf(item.workerId, data.workerTerminals)} />}
                        busy={terminalControl.busy}
                        onViewWorker={() => expandWorker(item.workerId)}
                        {...(item.takeover ? { onTakeControl: () => takeKeyboard(item.workerId, item.sessionId) } : {})} />
                    ))}
                  </section>
                )}

              </div>

              <div className="split" role="separator" aria-orientation="vertical" aria-label="Width of the Worker column"
                tabIndex={0} onPointerDown={onDividerDown} onPointerMove={onDividerMove} onKeyDown={nudgeDivider} />

              <aside className="work-side" aria-label="Reports, Live Activity and Workers">
                <div className="row side-view-tabs" role="tablist" aria-label="Second column">
                  <button type="button" role="tab" aria-selected={effectiveSideView === 'reports'}
                    className={effectiveSideView === 'reports' ? 'primary' : 'ghost'} onClick={() => setSideView('reports')}>Reports</button>
                  <button type="button" role="tab" aria-selected={effectiveSideView === 'activity'}
                    className={effectiveSideView === 'activity' ? 'primary' : 'ghost'} onClick={() => setSideView('activity')}>Live Activity</button>
                  <button type="button" role="tab" aria-selected={effectiveSideView === 'workers'}
                    className={effectiveSideView === 'workers' ? 'primary' : 'ghost'} onClick={() => setSideView('workers')}>
                    Workers <small>({jobWorkerCards.length} on a job)</small>
                  </button>
                </div>
                {/* Read-only. A figure this check did not measure stays Unknown. */}
                <DatabaseHealthStrip card={systems.find((card) => card.typeKey === 'supabase_project') ?? null} />
                {effectiveSideView === 'reports' && (
                  <ReportsPanel running={data.running} progress={data.progress} results={data.results} titles={titles}
                    needsYou={attentionCount} nowMs={nowMs} onOpenAttention={() => setMainCommandTool('attention')}
                    ownerGateViews={ownerGateWaiting.views} failures={failures} />
                )}
                {effectiveSideView === 'activity' && (
                  <LiveActivityLane
                    rows={liveActivity}
                    attentionCount={managementAttentionCount}
                    onOpenAttention={() => setMainCommandTool('attention')}
                  />
                )}
                <section className="cockpit-floor" aria-label="Workers" hidden={effectiveSideView !== 'workers'}>
                  <header className="row spread cockpit-workers-heading">
                    <h2>Workers</h2>
                    <span className="row">
                      <small className="muted">{jobWorkerCards.length} on a job{idleWorkerCount > 0 ? ` · ${idleWorkerCount} idle` : ''}</small>
                      {idleWorkerCount > 0 && (
                        <button type="button" className="link" onClick={() => setShowIdleWorkers((value) => !value)}>
                          {showIdleWorkers ? 'Hide idle' : `Show idle (${idleWorkerCount})`}
                        </button>
                      )}
                      <button type="button" className="link" aria-pressed={workerTerminalDebug}
                        title="Workers run without a terminal. This only shows or hides each Worker's interactive terminal, for debugging, in this browser."
                        onClick={toggleWorkerTerminalDebug}>
                        {workerTerminalDebug ? 'Hide terminals (debug)' : 'Show terminals (debug)'}
                      </button>
                      <button type="button" className="link" onClick={() => setSection('settings')}>Add / manage Workers</button>
                    </span>
                  </header>
                  {visibleWorkerCards.length === 0 && workerCards.length > 0 && (
                    <p className="muted">No Worker has a job right now. Brunel starts Workers when work needs them.</p>
                  )}
                  {(visibleWorkerCards.length > 0 || workerCards.length === 0) && <CockpitWorkers cards={visibleWorkerCards} machines={workerMachines} results={data.results}
                    aiProfiles={workerAiProfiles} nowMs={nowMs}
                    offeringChoices={workerOfferingChoices} offeringBusy={workerOffering.busy}
                    offeringError={workerOffering.error} onChangeOffering={changeWorkerOffering}
                    terminalWorkerId={workerTerminalOpen ? expandedWorkerId : null}
                    onOpenTerminal={expandWorker} onCloseTerminal={closeWorkerTerminal}
                    renderSurface={(workerId, active) => renderWorkerSurface(workerId, active)} />}
                </section>

              </aside>

            </main>
          )}

          {section === 'engine' && machineBoard.nodeId !== null && (
            <MachineBoardPage
              reader={reader}
              orgId={orgId}
              nodeId={machineBoard.nodeId}
              nowMs={nowMs}
              rows={data}
              machines={machineCapacity}
              meters={aiCapacityMeters}
              pendingApprovalsByWorker={NO_PENDING_APPROVALS}
              onClose={machineBoard.close}
              onSwitchMachine={machineBoard.open}
              onOpenWorker={expandWorker}
              onOpenTask={openWorkBoardDetail}
              onOpenUsage={openAiUsageDetail}
              onOpenCapacity={openMachineCapacity}
            />
          )}

          {section === 'engine' && machineBoard.nodeId === null && (
            <>
              <EngineRoomViewSelector view={engineRoomView} onView={setEngineRoomView} />
              {engineRoomView === 'live' ? (
                <>
                  <EngineRoom
                    model={engineRoomModel}
                    report={machinistReport}
                    onOpenMachine={openMachineDetail}
                    onOpenAiLane={openAiUsageDetail}
                    onOpenSystem={openSystemDetail}
                    onOpenWork={openWorkBoardDetail}
                    onReportToMainCommand={reportMachinistToMainCommand}
                  />
                  <EngineRoomRecords proofs={data.runtimeProofs} />
                </>
              ) : engineRoomView === 'history' ? (
                <EngineRoomHistory
                  results={data.results}
                  workers={data.workers}
                  reports={data.reports}
                  streams={data.streams}
                  projects={data.projects}
                  sessions={data.sessions}
                  commands={data.commands}
                  plans={data.planHistory}
                  audit={data.audit}
                  auditChatter={data.auditChatter}
                  streamId={streamId}
                  chat={chat}
                  titles={titles}
                  nowMs={nowMs}
                  ownerGateViews={ownerGateWaiting.views}
                  archivedPlans={proposalArchiveResult.archived}
                  onStreamChange={(nextStreamId) => {
                    setStreamId(nextStreamId);
                    setChat([]);
                  }}
                />
              ) : (
                <AiSessions client={client} orgId={orgId} project={project}
                  ownerVerified={owner.verified} onVerifyOwner={owner.verify} />
              )}
            </>
          )}

          {section === 'settings' && (
            <main className="work one settings-page" aria-label="Settings and options">
              <SettingsSectionNav />
              <SettingsGroup id="settings-account" label="Account">
                <Panel title="Profile & Security">
                  <ProfileSecuritySettings
                    client={client}
                    guard={guard}
                    ownerVerified={owner.verified}
                    onSignOut={onSignOut}
                    onChangePassword={() => setChangingPassword(true)}
                    onRefresh={() => void refresh()}
                    accountEmail={accountEmail}
                    serviceAddress={nodeConnection.url}
                  />
                </Panel>
                <Panel title="Sound Notifications">
                  <NotificationSoundSettings />
                </Panel>
              </SettingsGroup>
              <SettingsGroup id="settings-ai" label="AI & Workers">
                <WhoDoesWhat
                  client={client} guard={guard} orgId={orgId} revision={nowMs}
                  workers={activeWorkers} sessions={data.workforceSessions} nodes={data.nodes}
                  nodeOfferings={data.nodeOfferings} modelOfferings={data.modelOfferings}
                  preferences={data.workerOfferingPreferences} runtimeProofs={data.runtimeProofs}
                  launchCatalog={data.launchCatalog} nowMs={nowMs} plannerUrl={plannerUrl} onChanged={refresh}
                  outages={data.outages}
                  connectionStatuses={data.aiConnectionStatuses}
                  plannerStatus={data.plannerHostStatus[0] ?? null}
                />
                <Panel title="Overseer heartbeat">
                  <AutopilotHeartbeatSettings client={client} guard={guard} orgId={orgId}
                    read={autopilotHeartbeat} nowMs={nowMs} onChanged={refresh} />
                </Panel>
                <Panel title="AI usage & capacity">
                  <AiUsagePolicySettings client={client} orgId={orgId} policy={data.aiUsagePolicy} onSaved={refresh} />
                </Panel>
                <OutagesPanel outages={data.outages} nowMs={nowMs} recheckInstructions={data.recheckInstructions}
                  onCheckAgain={(row, onResult) => requestRuntimeRecheck({
                    row, guard, rpc: (name, args) => client.rpc(name, args), refresh, onResult,
                  })} />
              </SettingsGroup>
              <SettingsGroup id="settings-computers" label="Computers">
                <div className={outOfTouch.length > 0 ? 'settings-block tone-attention' : 'settings-block'}>
                  <h2>Computers{data.nodes.length > 0 && <span className="count">{data.nodes.length}</span>}</h2>
                  <ConnectionsPanel client={client} guard={guard} orgId={orgId} nodes={data.nodes} projects={data.projects} sessions={data.sessions} capacity={data.capacity} pressure={data.pressure} nowMs={nowMs} onChanged={refresh} />
                </div>
                <RemovedComputers client={client} orgId={orgId} refreshKey={data.nodes.length} />
                <details className="settings-install">
                  <summary>Add a computer</summary>
                  <AddNode client={client} guard={guard} orgId={orgId} connection={nodeConnection} />
                </details>
                <div className="settings-block">
                  <h2>Updates</h2>
                  <NodeUpdates client={client} guard={guard} orgId={orgId} versions={data.nodeVersions} releases={data.nodeReleases} running={data.running} onChanged={refresh}
                    rollout={computerRollout} />
                </div>
                <div className="settings-block">
                  <h2>GitHub merge route</h2>
                  <GithubMergeRouteSettings client={client} guard={guard} orgId={orgId} nodes={data.nodes} />
                </div>
                <Panel title="Connector Center">
                  <button type="button" onClick={() => {
                    setInspectorTab('systems');
                    setInspectorMode('drawer');
                  }}>Open Systems</button>
                </Panel>
              </SettingsGroup>
              <SettingsGroup id="settings-projects" label="Projects">
                <Panel title="Organization & Project">
                  <ContextSettings
                    orgs={orgs}
                    orgId={orgId}
                    projects={data.projects}
                    projectId={projectId}
                    onOrg={selectOrganization}
                    onProject={setProjectId}
                  />
                </Panel>
                <div className="settings-block">
                  <h2>Projects and repositories</h2>
                  <ProjectRepositorySettings
                    client={client} guard={guard} orgId={orgId}
                    projects={data.projects} repositories={data.repositories}
                    onChanged={refresh}
                  />
                </div>
                {data.projects.length > 0 && (
                  <Panel title="Projects" count={data.projects.length} closed>
                    <List items={data.projects} empty="No projects yet." render={(p) => p.name} />
                  </Panel>
                )}
                {data.missions.length > 0 && (
                  <Panel title="Missions" count={data.missions.length} closed>
                    <List items={data.missions} empty="No missions yet." render={(m) => <span className="row spread"><span>{m.title}</span><Status value={m.status} /></span>} />
                  </Panel>
                )}
              </SettingsGroup>
              <SettingsGroup id="settings-rules" label="Rules">
                <Panel title="Rules">
                  <RulesPanel client={client} guard={guard} orgId={orgId} ownerRules={ownerRules} planApproval={data.approvalPolicy?.plan_approval ?? 'auto'} onSaved={refresh} />
                </Panel>
              </SettingsGroup>
            </main>
          )}
        </div>

        <InspectorWorkbench
          mode={inspectorMode}
          widthPx={inspectorWidth}
          tab={inspectorTab}
          selection={inspectorSelection}
          viewportPx={viewportPx}
          onMode={setInspectorMode}
          onWidth={setInspectorWidth}
          onTab={setInspectorTab}
          railLabels={railLabels}
          onRailLabels={setRailLabels}
          notes={(
            <DeveloperNotepad
              client={client}
              orgId={orgId}
              onDiscuss={(staged) => {
                setNoteHandoff(staged);
                setDraft({ text: staged.text, at: Date.now() });
                setSection('control');
              }}
            />
          )}
          results={(
            <div className="inspector-results stack">
              <small className="muted">{runStats(data.results, nowMs).label}</small>
              {latest.length === 0 && <p className="muted">No finished tasks yet.</p>}
              <div className="inspector-results-list">
                {latest.map((result) => (
                  <button
                    key={result.assignment_id}
                    type="button"
                    className="inspector-result-item"
                    onClick={() => {
                      if (result.worker_id) {
                        expandWorker(result.worker_id);
                        setInspectorTab('work_result');
                        setInspectorMode('drawer');
                      }
                    }}
                  >
                    <RunResult result={result} workers={data.workers} titles={titles} view={recentRunViews.get(result.assignment_id)} awaitingOwner={ownerGateWaiting.views.get(result.assignment_id)?.awaitingOwner === true} />
                  </button>
                ))}
              </div>
              <button type="button" className="link" onClick={() => {
                setSection('engine');
                setEngineRoomView('history');
              }}>
                Every finished task in Engine Room History
              </button>
            </div>
          )}
          workBoard={(
            <WorkBoard
              orgId={orgId}
              roadmap={roadmapItems}
              plans={[...data.planHistory, ...data.plans]}
              missionTasks={data.missionTasks}
              taskActions={data.taskActions}
              jobRecoveries={data.jobRecoveries}
              onDiscardTask={(action, onResult) => discardTask({
                action, guard, rpc: (name, args) => client.rpc(name, args),
                reread: async () => {
                  const read = await reader.taskAction(orgId, action.project_id, action.task_id);
                  return read.ok ? read.rows[0] ?? null : null;
                },
                refresh, onResult,
              })}
              onRefresh={refresh}
              lastRefreshedAt={lastFullRefreshAt}
              onDraftWork={stageWorkBoardCard}
              onOpenRoadmapItem={openRoadmapItem}
              onReviewPlans={() => setReviewingPlans(true)}
              onOpenWorker={expandWorker}
              onStopRun={stopWorkBoardRun}
              stopReceipt={workBoardStopReceipt}
              onRetryTask={retryWorkBoardTask}
              loadHistoryPage={(page, pageSize) => loadWorkBoardHistory(reader, orgId, page, pageSize)}
              running={data.running}
              results={data.results}
              commandPlans={data.commandPlans}
              workerTerminals={data.workerTerminals}
              approvals={[]}
              workers={data.workers}
              projects={data.projects}
              capacity={data.capacity}
              nowMs={nowMs}
            />
          )}
          roadmap={(
            <RoadmapPanel
              client={client}
              orgId={orgId}
              projects={data.projects}
              items={roadmapItems}
              focusItemId={roadmapFocusId}
              onWorkItem={(item) => stageExistingWork({
                title: item.title,
                projectId: item.project_id,
                projectName: item.project_name,
              })}
              onChanged={() => { void refresh(); }}
            />
          )}
          usage={(
            <AiUsageDetail
              meters={aiCapacityMeters}
              selectedKey={selectedUsageKey}
              onSelect={setSelectedUsageKey}
              focusRequest={usageFocusRequest}
              policy={data.aiUsagePolicy}
              readings={data.providerUsage}
              measurements={data.usageMeasurements}
              runUsage={data.runPoolUsage}
              projects={data.projects}
              tasks={data.missionTasks}
              running={data.running}
              results={data.results}
            />
          )}
          systems={(
            <ConnectorCenter
              client={client}
              guard={guard}
              orgId={orgId}
              nodes={data.nodes}
              providerUsage={data.providerUsage}
              connectionStatuses={data.aiConnectionStatuses}
              localRuntimeStatuses={data.aiLocalRuntimeStatuses}
              nodeOfferings={data.nodeOfferings}
              modelOfferings={data.modelOfferings}
              codexAutonomyStatuses={data.codexAutonomyStatuses}
              registry={connectorRegistry}
              systems={systems}
              focusedSystemType={selectedSystemType}
              onRefreshRegistry={refreshConnectorRegistry}
              onChanged={refresh}
              onOpenLocalDetail={(nodeId) => {
                const meter = aiCapacityMeters.find((item) =>
                  item.nodeId === nodeId
                  && item.provider === 'ollama'
                  && item.runtime === 'ollama');
                if (!meter) return;
                setSelectedUsageKey(meter.key);
                setInspectorTab('usage');
                setInspectorMode('drawer');
                setUsageFocusRequest((value) => value + 1);
              }}
            />
          )}
          machines={(
            <MachineCapacityPanel
              client={client}
              guard={guard}
              orgId={orgId}
              machines={machineCapacity}
              selectedNodeId={selectedMachineId}
              onSelect={setSelectedMachineId}
              contract={data.capacityContract ?? DEFAULT_CAPACITY_GUARD_CONTRACT}
              limitChanges={machineLimitChanges}
              onChanged={refresh}
            />
          )}
          ideDecision={ideDecision}
          ideBusy={openWorkspace.busy}
          ideError={openWorkspace.error}
          ideOk={ideLaunchOk}
          onOpenIde={() => {
            if (ideDecision.action !== 'open') return;
            setIdeLaunchOk(null);
            void openWorkspace.open(orgId, ideDecision.nodeId, ideDecision.projectId, ideDecision.adapterKey)
              .then((ok) => { if (ok) setIdeLaunchOk(`Asked ${ideDecision.nodeName} to open the worktree.`); });
          }}
        />

        {section !== 'control' && (
          <ComposerDraftView controller={mainCommandComposer}>{(mainCommandDraft) => (
          <FloatingMainCommand
            expanded={floatingMainCommandExpanded}
            onExpandedChange={setFloatingMainCommandExpanded}
            activeConversation={floatingConversation}
            timeline={mainCommandConversation.visibleTimeline}
            unreadCount={0}
            managementAttentionCount={managementAttentionCount}
            onOpenManagementAttention={() => {
              setMainCommandTool('attention');
              setSection('control');
            }}
            selectedParticipants={replyTargets}
            identities={replyIdentities}
            onToggleParticipant={toggleReplyTarget}
            workers={workerChatState}
            eligibleWorkerIds={eligibleWorkerIds}
            selectedWorkers={workerReplyTargets}
            onToggleWorker={toggleWorkerReplyTarget}
            onToggleAllWorkers={toggleAllWorkerReplyTargets}
            mentionCandidates={mentionCandidates}
            onMentionSelect={selectMentionRecipient}
            onDirectReply={directReply}
            draft={mainCommandDraft}
            onDraftChange={mainCommandComposer.setContent}
            onSend={mainCommandComposer.send}
            sending={mainCommandComposer.sending}
            commandReceipt={commandReceiptBook.pending}
            controlReceipts={commandReceiptBook.byCommand}
            controlReceiptActions={commandReceiptActions}
            onOpenFullCommand={() => setSection('control')}
          />
          )}</ComposerDraftView>
        )}

        <LiveDock
          events={liveEvents}
          attentionCount={attentionCount}
          onWorkerSelect={expandWorker}
          overview={<SnapshotStrip stats={stats} />}
          activity={liveDockActivity}
          machines={liveDockMachines}
          selectedMachineId={inspectorTab === 'machines' && inspectorMode !== 'hidden' ? selectedMachineId : null}
          onMachineSelect={openMachineDetail}
          usageMeters={aiCapacityMeters}
          usageAdvisories={aiUsageAdvisories}
          selectedUsageKey={inspectorTab === 'usage' && inspectorMode !== 'hidden' ? selectedUsageKey : null}
          onUsageSelect={(key) => {
            setSelectedUsageKey(key);
            setInspectorTab('usage');
            setInspectorMode('drawer');
            setUsageFocusRequest((value) => value + 1);
          }}
          systems={systems}
          selectedSystemType={inspectorTab === 'systems' && inspectorMode !== 'hidden' ? selectedSystemType : null}
          onSystemSelect={(typeKey) => {
            setSelectedSystemType(typeKey);
            setInspectorTab('systems');
            setInspectorMode('drawer');
          }}
          expanded={liveDockExpanded}
          onExpandedChange={setLiveDockExpanded}
          activityIndicator={activityIndicator}
          delivery={liveDelivery}
          census={dockCensus}
        />
      </div>

      {workflowGuideOpen && <WorkflowGuide onClose={closeWorkflowGuide} />}


      {reviewingPlans && (
        <PlanDrawer client={client} guard={guard} plans={data.plans} projects={data.projects}
          commands={data.commands} routing={data.workerRouting} workers={routingWorkers} seats={planSeats}
          nowMs={nowMs} archive={proposalArchiveResult.rows} onDecided={refresh}
          onDiscuss={(_plan, prefill) => {
            // Discuss plan is not Approve or Reject: the plan stays pending. The
            // owner returns to the command box with a non-mutating discussion prompt
            // (explain the plan; do not draft/execute a replacement yet).
            setNoteHandoff(null);
            setDraft({ text: prefill, at: Date.now() });
            setSection('control');
            setReviewingPlans(false);
          }}
          onClose={() => setReviewingPlans(false)} />
      )}

      {changingPassword && (
        <div className="password-change-overlay" role="presentation">
          <section className="card auth password-change-dialog" role="dialog" aria-modal="true" aria-labelledby="change-password-title">
            <h2 id="change-password-title">Change password</h2>
            <p className="muted">This changes the password on your existing Supabase account. Your Google identity and Brunel ownership stay linked.</p>
            <ResetPasswordForm client={client} onDone={() => setChangingPassword(false)} />
            <button type="button" className="link" onClick={() => setChangingPassword(false)}>Cancel</button>
          </section>
        </div>
      )}

    </div>
  );
}

/** The status dot an attention panel shows, read from that Worker's own terminal row. */
function workerStatusOf(workerId: string, rows: readonly WorkerTerminalRow[]): string {
  const row = rows.find((item) => item.worker_id === workerId);
  return row === undefined ? 'unknown' : cockpitWorkerStatus(row);
}

/** Icon-only Worker surface collapse: the modal/workspace owns Escape semantics. */
function WorkerCollapseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"
      fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 15l6-6 6 6" />
    </svg>
  );
}

/**
 * A Worker's own surface: its live read-only run log and read-only activity. Its interactive
 * terminal is a debug view, shown only when this browser has turned that on - or while the
 * Worker's own terminal is asking the owner something, which only they can answer there.
 */
function WorkerSurface({ client, guard, orgId, worker, session, reader, active, onChanged, onCollapse, currentRun, currentProgress, terminalDebug, terminalWaiting, aiProfile, onApplyAiProfile, onDiscard }: {
  client: SupabaseClient; guard: Guard; orgId: string; worker: Worker;
  session: WorkforceSession | null; reader: Reader;
  aiProfile: AiExecutionProfileView | null;
  onApplyAiProfile: (view: AiExecutionProfileView, change: AiExecutionProfileChange) => Promise<AiExecutionProfileApplyResult>;
  /** False while this Worker is collapsed. The surface stays mounted either way. */
  active: boolean;
  onChanged: () => Promise<void>; onCollapse: () => void;
  currentRun: RunningTask | null;
  currentProgress: RunProgress | null;
  /** Whether this browser shows the Worker's interactive terminal. Off by default. */
  terminalDebug: boolean;
  /** #1133 true while this Worker's own terminal is asking the owner something. */
  terminalWaiting: boolean;
  /** #650 present only when the Worker is not working and its terminal/session has ended. */
  onDiscard: ((onResult: (result: DiscardResult) => void) => Promise<void>) | null;
}) {
  const badge = workerBadge(worker.status, 0);
  const [workerView, setWorkerView] = useState<WorkerSurfaceView>(
    () => (active ? workerSurfaceOpeningView(terminalWaiting) : 'log'),
  );
  const terminalShown = workerTerminalShown({ terminalDebug, terminalWaiting, active, view: workerView });
  const terminalWaitingNow = useRef(terminalWaiting);
  terminalWaitingNow.current = terminalWaiting;
  // This Worker's run log, joined from the recorded progress of its own run and nothing else.
  const [runLog, setRunLog] = useState<WorkerRunLogState | null>(null);
  useEffect(() => {
    setRunLog((current) => nextWorkerRunLog(current, currentRun, currentProgress));
  }, [currentRun, currentProgress]);
  const stopRun = useStopRun(client);
  const [stopReceipt, setStopReceipt] = useState<ControlReceiptView | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardResult, setDiscardResult] = useState<DiscardResult | null>(null);

  const discard = () => {
    if (onDiscard === null) return;
    setDiscarding(true);
    setDiscardResult(null);
    void onDiscard((result) => {
      setDiscardResult(result);
      setConfirmDiscard(false);
    }).finally(() => setDiscarding(false));
  };

  const stopCurrentRun = () => {
    if (currentRun === null) return;
    setStopReceipt(sentReceipt());
    void guard('post_owner_command', async () => {
      if (await stopRun.stop(orgId, currentRun.assignment_id, 'Stopped directly from the Worker card by the owner')) {
        await onChanged();
        return;
      }
      setStopReceipt(receiptFromSubmitRefusal(stopRun.lastRefusal.current ?? ''));
    });
  };

  // Collapsed, the surface goes back to its run log. Opened while its terminal is waiting on
  // the owner, it opens on that terminal. Only opening chooses the view: a question that
  // arrives while the owner is reading something else adds the tab and moves nothing, so a
  // key already on its way is never typed into a terminal the owner did not choose.
  useEffect(() => {
    setWorkerView(active ? workerSurfaceOpeningView(terminalWaitingNow.current) : 'log');
  }, [active]);
  // The terminal view goes when the debug switch is turned off; the run log takes its place.
  // A terminal that is waiting on the owner stays.
  useEffect(() => {
    if (!terminalDebug && !terminalWaitingNow.current) setWorkerView((view) => (view === 'terminal' ? 'log' : view));
  }, [terminalDebug]);

  return (
    <section className="workersurface" aria-label={`Worker ${worker.number}`}>
      <header className="row spread drawer-head">
        <span className="row wrap">
          <strong><span className="worker-no">{worker.number}</span> {worker.name}</strong>
          {currentRun !== null && <small className="muted">Working · {currentRun.title}</small>}
        </span>
        <span className="row">
          {currentRun !== null && (
            <button type="button" disabled={stopRun.busy} onClick={stopCurrentRun}>
              Stop now
            </button>
          )}
          {stopReceipt !== null && <ControlReceiptNote view={stopReceipt} action="Stop now" />}
          {currentRun === null && onDiscard !== null && !confirmDiscard && (
            <button type="button" onClick={() => { setDiscardResult(null); setConfirmDiscard(true); }}>
              Discard Worker
            </button>
          )}
          <button type="button" className="link"
            aria-label={`Collapse Worker ${worker.number} ${worker.name}`}
            title={`Collapse Worker ${worker.number} ${worker.name}`}
            onClick={onCollapse}>
            <WorkerCollapseIcon />
          </button>
        </span>
      </header>
      {aiProfile !== null && (
        <AiExecutionProfileStrip profile={aiProfile} onApply={(change) => onApplyAiProfile(aiProfile, change)} />
      )}
      {stopRun.error !== null && <p role="alert" className="worker-stop-error">{stopRun.error}</p>}
      {confirmDiscard && onDiscard !== null && (
        <div className="row wrap" role="group" aria-label={`Discard Worker ${worker.number}`}>
          <small>
            Discard Worker {worker.number}? It leaves the active Workers and Main Command recipients.
            Its runs, tasks, terminal history and conversations are kept, and its number is never reused.
          </small>
          <button type="button" disabled={discarding} onClick={discard}>Discard Worker</button>
          <button type="button" className="link" disabled={discarding} onClick={() => setConfirmDiscard(false)}>Keep</button>
        </div>
      )}
      {discardResult !== null && (
        <p role={discardResult.ok ? 'status' : 'alert'} className={discardResult.ok ? 'muted' : 'worker-stop-error'}>
          {discardResult.message}
        </p>
      )}
      <nav className="worker-tabs row" aria-label={`Worker ${worker.number} views`}>
        {workerSurfaceViews(terminalShown).map((view) => (
          <button key={view} type="button" className={workerView === view ? 'active' : ''}
            data-waiting-for-you={view === 'terminal' && terminalWaiting ? true : undefined}
            aria-pressed={workerView === view} onClick={() => setWorkerView(view)}>
            {view === 'terminal' ? workerTerminalViewLabel({ terminalDebug, terminalWaiting }) : WORKER_SURFACE_VIEW_LABEL[view]}
          </button>
        ))}
      </nav>

      {/* The Worker's process output, read-only. This is what a run is watched through. */}
      {workerView === 'log' && <WorkerRunLog worker={worker} log={runLog} />}

      {/* Off by default: the Worker's interactive terminal. A headless run does not use it.
          It is here in debug, and while this Worker's own terminal is asking the owner
          something (#1133), which only a turn still run in the terminal can do.
          While it is shown, keep it mounted across tab switches. Hiding it must not tear down
          its PTY, Realtime lease, input ACK state, or scrollback. */}
      {terminalShown && (
        <div className="worker-view" hidden={workerView !== 'terminal'}>
          <WorkerTerminal client={client} guard={guard} orgId={orgId} workerId={worker.id}
            workerNumber={worker.number} session={session} reader={reader}
            visible={active && workerView === 'terminal'} />
        </div>
      )}

      {workerView === 'activity' && (
        <section className={`statuspanel tone-${badge.tone}`} aria-label="Worker activity">
          <h3>Activity</h3>
          <p className="status-line"><span className={`dot dot-${badge.tone}`} aria-hidden="true" />{badge.label}</p>
          {session && <small className="muted">AI: {session.offering_key} · node session {session.id.slice(0, 8)}</small>}
          {(worker.status === 'completed' || worker.status === 'idle') && (
            <small className="muted">Idle. Assign new work through Main Command so the plan and routing stay in Brunel's chain of command.</small>
          )}
        </section>
      )}
    </section>
  );
}

function List<T extends { id?: string }>({ items, render, empty }: { items: readonly T[]; render: (item: T) => React.ReactNode; empty: string }) {
  if (items.length === 0) return <p className="muted">{empty}</p>;
  return <ul className="list">{items.map((item, i) => <li key={item.id ?? i}>{render(item)}</li>)}</ul>;
}

function Status({ value }: { value: string }) {
  return <span className={`status status-${value}`}>{value.replace('_', ' ')}</span>;
}

/** Renders its children with the live Main Command draft, so only they re-render as it changes. */
function ComposerDraftView({ controller, children }: {
  controller: MainCommandComposerController;
  children: (draft: string) => React.ReactNode;
}) {
  const draft = useComposerDraft(controller);
  return <>{children(draft)}</>;
}

function CommandComposer({
  client,
  guard,
  orgId,
  controller,
  voiceReplies,
  onVoiceReplies,
  nodeConnection,
  height,
  onHeight,
  mentionCandidates,
  onMentionSelect,
  noteHandoff,
  onDismissNoteHandoff,
  projectId,
  workerIds,
  projects,
  workers,
  replyingTo,
  onCancelReply,
  commandReceipt = null,
}: {
  client: SupabaseClient;
  guard: Guard;
  orgId: string;
  controller: MainCommandComposerController;
  voiceReplies: boolean;
  onVoiceReplies: (on: boolean) => void;
  nodeConnection: { url: string; publishableKey: string };
  height: number | null;
  onHeight: (px: number) => void;
  mentionCandidates: readonly MainCommandMentionCandidate[];
  onMentionSelect: (candidate: MainCommandMentionCandidate) => void;
  noteHandoff: NoteHandoffStage | null;
  onDismissNoteHandoff: () => void;
  projectId: string;
  workerIds: readonly string[];
  projects: readonly { readonly id: string; readonly name: string }[];
  workers: readonly { readonly id: string; readonly number: number; readonly name: string }[];
  replyingTo: MainCommandReplyDraft | null;
  onCancelReply: () => void;
  commandReceipt?: ControlReceiptView | null;
}) {
  const box = useRef<HTMLTextAreaElement | null>(null);
  // Subscribed here, not in the room: a keystroke re-renders this composer only.
  const content = useComposerDraft(controller);
  const [mentionCaret, setMentionCaret] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionSuggestions = useMemo(
    () => mainCommandMentionSuggestions(content, mentionCaret, mentionCandidates),
    [content, mentionCaret, mentionCandidates],
  );
  const dictationBase = useRef<string | null>(null);
  // #805 owner: one Send button sends a typed or a dictated draft. Sending mid-dictation
  // stops listening, and the recognizer's late final result must not refill the box.
  const stopDictation = useRef<(() => void) | null>(null);
  const dictating = useRef(false);
  const dropDictationFinal = useRef(false);
  const sendDraft = () => {
    if (dictating.current) {
      dropDictationFinal.current = true;
      stopDictation.current?.();
    }
    dictating.current = false;
    dictationBase.current = null;
    controller.send();
  };

  useEffect(() => {
    if (controller.draftAt !== null) box.current?.focus();
  }, [controller.draftAt]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    sendDraft();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionSuggestions.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setMentionIndex((current) => (current + direction + mentionSuggestions.length) % mentionSuggestions.length);
        return;
      }
      if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
        event.preventDefault();
        const candidate = mentionSuggestions[Math.min(mentionIndex, mentionSuggestions.length - 1)]!;
        const next = removeSelectedMainCommandMention(content, event.currentTarget.selectionStart);
        onMentionSelect(candidate);
        controller.setContent(next.content);
        setMentionCaret(next.caret);
        setMentionIndex(0);
        queueMicrotask(() => box.current?.setSelectionRange(next.caret, next.caret));
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMentionCaret(-1);
        return;
      }
    }
    const direction = composerHistoryDirection(
      {
        key: event.key,
        shiftKey: event.shiftKey,
        isComposing: event.nativeEvent.isComposing,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
      },
      event.currentTarget.selectionStart,
      event.currentTarget.selectionEnd,
      content.length,
      controller.historyWalking(),
    );
    if (direction !== null) {
      const recalled = controller.recallHistory(direction, content);
      if (recalled !== null) {
        event.preventDefault();
        dictationBase.current = null;
        controller.setContentFromHistory(recalled);
        queueMicrotask(() => box.current?.setSelectionRange(recalled.length, recalled.length));
        return;
      }
    }
    if (!sendsOnEnter({
      key: event.key,
      shiftKey: event.shiftKey,
      isComposing: event.nativeEvent.isComposing,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
    })) return;
    event.preventDefault();
    sendDraft();
  };

  const noteShareText = noteHandoff === null
    ? ''
    : (content || noteHandoff.text);
  return (
    <>
    <form onSubmit={onSubmit} className="stack" data-reload-preserved="main-command">
      {replyingTo !== null && (
        <div className="reply-quote composing" data-reply-kind={replyingTo.ref.kind} data-testid="main-command-reply-quote">
          <strong>Replying to {replyingTo.author}</strong>
          <span>{replyingTo.text}</span>
          <button type="button" className="reply-quote-cancel" aria-label="Cancel reply" title="Cancel reply" onClick={onCancelReply}>×</button>
        </div>
      )}
      {noteHandoff !== null && (
        <NoteHandoffChipView
          chip={noteHandoff.chip}
          draftText={noteShareText}
          onDismiss={onDismissNoteHandoff}
        />
      )}
      <textarea
        ref={box}
        aria-label="Command"
        placeholder="e.g. 1 take on the checkout fix · 3 stop · what is worker 2 doing?"
        maxLength={16000}
        data-composer-authority={controller.composerAuthority}
        className="command-box"
        style={{ height: `${height ?? COMPOSER_DEFAULT_PX}px` }}
        onPointerUp={(event) => {
          const next = clampComposerHeight(event.currentTarget.offsetHeight, window.innerHeight);
          if (next !== (height ?? COMPOSER_DEFAULT_PX)) onHeight(next);
        }}
        value={content}
        onChange={(event) => {
          dictationBase.current = null;
          controller.setContent(event.target.value);
          setMentionCaret(event.target.selectionStart);
          setMentionIndex(0);
        }}
        onClick={(event) => setMentionCaret(event.currentTarget.selectionStart)}
        onKeyUp={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') setMentionCaret(event.currentTarget.selectionStart);
        }}
        onKeyDown={onKeyDown}
        required
      />
      <MainCommandMentionMenu
        suggestions={mentionSuggestions}
        activeIndex={Math.min(mentionIndex, Math.max(0, mentionSuggestions.length - 1))}
        onChoose={(candidate) => {
          const next = removeSelectedMainCommandMention(content, mentionCaret);
          onMentionSelect(candidate);
          controller.setContent(next.content);
          setMentionCaret(next.caret);
          setMentionIndex(0);
          queueMicrotask(() => {
            box.current?.focus();
            box.current?.setSelectionRange(next.caret, next.caret);
          });
        }}
      />
      <div className="command-composer-actions">
        <DictateButton
          stopRef={stopDictation}
          onInterim={(text) => controller.updateContent((current) => {
            if (text === '' || dropDictationFinal.current) return current;
            dictating.current = true;
            if (dictationBase.current === null) dictationBase.current = current;
            return appendDictation(dictationBase.current, text).slice(0, 16000);
          })}
          onText={(text) => controller.updateContent((current) => {
            dictating.current = false;
            if (dropDictationFinal.current) {
              // Already sent with its interim words; the late final is not a new draft.
              dropDictationFinal.current = false;
              dictationBase.current = null;
              return current;
            }
            const base = dictationBase.current ?? current;
            dictationBase.current = null;
            return appendDictation(base, text).slice(0, 16000);
          })}
        />
        <label className="switch system-voice-fallback command-speak-toggle" title="Read replies aloud with the saved system voice">
          <input type="checkbox" checked={voiceReplies} onChange={(event) => onVoiceReplies(event.target.checked)} />
          <span aria-hidden="true" /> Speak
        </label>
        <button
          type="submit"
          className="primary command-send"
          disabled={controller.sending || !content.trim()
            || controller.composerAuthority === 'none'}
        >
          Send
        </button>
      </div>
      {commandReceipt !== null && <ControlReceiptNote view={commandReceipt} action={SENT_MESSAGE} />}
      {controller.sendError && <p role="alert">{controller.sendError}</p>}
    </form>
    </>
  );
}

/** One running task, with the button that frees its worker when it is going nowhere. */

/**
 * Output read as a log rather than dumped: monospace and bounded, scrolling in
 * its own box, and reachable from the keyboard so it can be scrolled without a
 * mouse. It carries no colour of its own beyond the palette's own surfaces.
 */
function LogView({ text, label }: { text: string; label: string }) {
  return <pre className="logview" tabIndex={0} role="group" aria-label={label}>{plainTerminalText(text)}</pre>;
}

/**
 * One finished task: what it was asked to do, its summary, who did it, how long it took and what ran it,
 * and the end of what the worker printed when there is any. Anything the run
 * did not record says so in words rather than going blank.
 */
function RunResult({ result, workers, titles, view, awaitingOwner }: {
  result: FinishedRun;
  workers: readonly Worker[];
  titles: ReadonlyMap<string, string>;
  view: RecentRunView | undefined;
  awaitingOwner: boolean;
}) {
  const took = durationPhrase(runDurationMs(result));
  // The tool this run was given, when something else ran it. Named, not hinted at.
  const passedOver = handoverPhrase(result);
  const summary = view?.summary ?? result.summary;
  return (
    <div className="stack">
      <span className="row spread"><strong>{runTaskTitle(result, titles)}</strong>{awaitingOwner
        ? <span className="status status-awaiting-owner">{MERGED_AWAITING_OWNER}</span>
        : <Status value={result.success ? 'completed' : 'failed'} />}</span>
      {view?.supersededByLabel !== null && view?.supersededByLabel !== undefined && (
        <small className="muted">{view.supersededByLabel}</small>
      )}
      {summary !== '' && <span>{summary}</span>}
      <small className="muted">
        {workerPhrase(result, workers)}{' · took '}{took}{' · ran with '}{toolPhrase(result)}
        {passedOver === null ? '' : ` (handed over; ${passedOver} was assigned)`}
      </small>
      {result.output_tail && (
        <details>
          <summary className="muted">What the worker printed</summary>
          <LogView text={result.output_tail} label="What the worker printed" />
        </details>
      )}
    </div>
  );
}
